/**
 * SUPERADMIN.JS — LOGIMES Platform
 * God Mode Dashboard — Super Admin uniquement
 * Tabs: Vue d'ensemble · Users · Sociétés CRUD · Stats/Société · Factures · Archive · Tracabilité · Avoirs · Sandbox · Système
 * Hérite: Utilisateur + Admin + propres (CRUD sociétés, gérer rôles, stats par société)
 * Auteur: PFE 2026 — ESEN Manouba
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIDDLEWARE_URL = process.env.REACT_APP_API_URL;

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

const ROLE_CONFIG = {
  super_admin: { label: "Super Admin", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", icon: "◆" },
  admin:       { label: "Admin",       color: "#0EA5E9", bg: "#F0F9FF", border: "#BAE6FD", icon: "▸" },
  user:        { label: "Utilisateur", color: "#64748B", bg: "#F8FAFC", border: "#E2E8F0", icon: "○" },
};

const NAV_SECTIONS = [
  {
    label: "God Mode", color: "#7C3AED",
    items: [
      { icon: "◆",  label: "Vue d'ensemble",   tab: "dashboard"     },
      { icon: "👥", label: "Gestion Users",     tab: "users"         },
      { icon: "🏢", label: "Sociétés CRUD",     tab: "societes"      },
      { icon: "📊", label: "Stats par Société", tab: "stats"         },
      { icon: "✏️", label: "Modifications",     tab: "modifications" },
      { icon: "📡", label: "Event Bus",         tab: "events"        },
    ],
  },
  {
    label: "ERP & Facturation", color: "#6c63ff",
    items: [
      { icon: "🧾", label: "Toutes Factures",   tab: "factures"      },
      { icon: "👥", label: "Clients",            tab: "clients"       },
    ],
  },
  {
    label: "Tracabilité & Archivage", color: "#43b89c",
    items: [
      { icon: "🔍", label: "Tracabilité",        tab: "tracabilite"   },
      { icon: "🔔", label: "Alertes",            tab: "alertes"       },
      { icon: "🗄️", label: "Archive ANCE",      tab: "archive"       },
      { icon: "📊", label: "Statistiques",       tab: "stats"         },
    ],
  },
  {
    label: "Fonctions Avancées", color: "#f59e0b",
    items: [
      { icon: "📝", label: "Avoirs",             tab: "avoirs"        },
      { icon: "🧪", label: "Sandbox ANCE",       tab: "sandbox"       },
      { icon: "📴", label: "Mode Offline",       tab: "avoirs"        },
    ],
  },
  {
    label: "Paramètres", color: "#9b59b6",
    items: [
      { icon: "⚙️", label: "Système",            tab: "systeme"       },
      { icon: "✅", label: "Rapport Tests",      tab: "tests"         },
    ],
  },
];

const TABS = [
  { id: "dashboard",   label: "Vue d'ensemble",   icon: "◈"   },
  { id: "users",       label: "Users & Rôles",    icon: "👥"  },
  { id: "societes",    label: "Sociétés CRUD",    icon: "🏢"  },
  { id: "stats",       label: "Stats/Société",    icon: "📊"  },
  { id: "factures",    label: "Factures",          icon: "🧾"  },
  { id: "clients",     label: "Clients",           icon: "👥"  },
  { id: "tracabilite", label: "Tracabilité", icon: "🔍"  },
  { id: "alertes",     label: "Alertes",     icon: "🔔"  },
  { id: "archive",     label: "Archive",     icon: "🗄️" },
  { id: "avoirs",      label: "Avoirs",             icon: "📝"  },
  { id: "sandbox",     label: "Sandbox",            icon: "🧪"  },
  { id: "systeme",     label: "Système",            icon: "⚙️"  },
  { id: "tests",       label: "Tests",              icon: "✅" },
  { id: "events", label: "Event Bus", icon: "🔔" },
  { id: "modifications", label: "Demandes Modif", icon: "✏️" },
  { id: "assignments", label: "Assigner Société", icon: "🏢" },
];

export default function SuperAdmin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab,        setActiveTab]        = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toast,            setToast]            = useState(null);

  // Users
  const [users,         setUsers]         = useState([]);
  const [usersLoading,  setUsersLoading]  = useState(true);
  const [search,        setSearch]        = useState("");
  const [filterRole,    setFilterRole]    = useState("all");
  const [hoveredRow,    setHoveredRow]    = useState(null);
  const [roleModal,     setRoleModal]     = useState(null);
  const [selectedRole,  setSelectedRole]  = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [userStats,     setUserStats]     = useState({ total: 0, super_admin: 0, admin: 0, user: 0 });

  // Middleware data
  const [globalStats,   setGlobalStats]   = useState(null);
  const [alerts,        setAlerts]        = useState([]);
  const [certStatus,    setCertStatus]    = useState(null);
  const [companies,     setCompanies]     = useState([]);
  const [factures,      setFactures]      = useState([]);
  const [clients,       setClients]       = useState([]);
  const [archive,       setArchive]       = useState([]);
  const [archiveStats,  setArchiveStats]  = useState(null);
  const [tracabilite,   setTracabilite]   = useState(null);
  const [tracabiliteSelectedSoc, setTracabiliteSelectedSoc] = useState(null); 
  const [avoirs,        setAvoirs]        = useState([]);
  const [offlineQueue,  setOfflineQueue]  = useState([]);
  const [sandboxRes,    setSandboxRes]    = useState(null);
  const [middleLoading, setMiddleLoading] = useState(true);

  // ── Modifier société (depuis Admin.js) ──
const [editSocAdmin,   setEditSocAdmin]   = useState(null);
const [editFormAdmin,  setEditFormAdmin]  = useState({});
const [editStepAdmin,  setEditStepAdmin]  = useState(1);

  // Sociétés CRUD
  const [showSocModal,   setShowSocModal]   = useState(false);
  const [editSoc,        setEditSoc]        = useState(null);
  const [socForm,        setSocForm]        = useState({ name: "", tax_id: "", address: "", phone: "", email: "" });
  const [socLoading,     setSocLoading]     = useState(false);
  const [confirmDelSoc,  setConfirmDelSoc]  = useState(null);
  const [pendingSocietes, setPendingSocietes] = useState([])
  const [socConfirm,     setSocConfirm]     = useState(false);

  // Stats par société
  const [selectedSocStat, setSelectedSocStat] = useState(null);
  const [archiveSelectedSoc, setArchiveSelectedSoc] = useState(null);
  const [socStats,         setSocStats]        = useState(null);
  const [socStatsLoading,  setSocStatsLoading] = useState(false);

  // DSS Submit
  const [dssNumFacture, setDssNumFacture] = useState("");
  const [dssLoading,    setDssLoading]    = useState(false);
  const [dssResult,     setDssResult]     = useState(null);

  const [events, setEvents] = useState([]);
  const [eventNumFacture, setEventNumFacture] = useState("");
  const [eventType, setEventType] = useState("INVOICE_CREATED");
  const [eventLoading, setEventLoading] = useState(false);

  // Sandbox
  const [sbNumFacture, setSbNumFacture] = useState("");
  const [sbLoading,    setSbLoading]    = useState(false);
  const [sbSociete, setSbSociete] = useState(null);

  const [dashboardData, setDashboardData] = useState(null);

  const [clientFilter, setClientFilter] = useState("Tous");

  const [modRequests, setModRequests] = useState([]);
  const [modReqLoading, setModReqLoading] = useState(false);
  const [modActionLoading, setModActionLoading] = useState({});

  // ── Assignments ──
const [assignments,   setAssignments]  = useState([]);
const [assignLoading, setAssignLoading] = useState(false);
const [assignForm,    setAssignForm]    = useState({
  admin_id:     "",
  societe_id:   "",
  commentaire:  "",
  factures_ids: []
});
const [assignResult,  setAssignResult]  = useState(null);
const [admins,        setAdmins]        = useState([]);

  const [allPlatformSocietes, setAllPlatformSocietes] = useState([]);
  const [socShowAll, setSocShowAll] = useState(false);


const [bridgeLimit, setBridgeLimit] = useState(
  parseInt(localStorage.getItem("bridge_exercice")) || new Date().getFullYear()
);

  useEffect(() => {
    if (getCookie("role") !== "super_admin") { navigate("/home"); return; }
    fetchUsers();
    fetchAllMiddleware();
    fetchModRequests();
    fetchAssignments();
    fetchAdmins();
    fetchSocietes();
    syncSocietes();
    
  }, []);

  const token   = () => getCookie("token");
  const authH   = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ─── FETCH USERS ──────────────────────────────────────────
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res  = await fetch(`${BACKEND_URL}/api/auth/admin/users`, { headers: authH() });
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.users || [];
      setUsers(list);
      setUserStats({
        total:       list.length,
        super_admin: list.filter(u => u.role === "super_admin").length,
        admin:       list.filter(u => u.role === "admin").length,
        user:        list.filter(u => u.role === "user").length,
      });
    } catch { showToast("Erreur chargement users", "error"); }
    setUsersLoading(false);
  };

  // ─── FETCH ALL MIDDLEWARE ─────────────────────────────────
const fetchAllMiddleware = async () => {
  setMiddleLoading(true);

  try {
    const [stats, al, cert, comp, fact, cli, arch, archSt, trace, av, off, sb] = await Promise.all([
      fetch(`${MIDDLEWARE_URL}/archive/statistics/full`,  { headers: authH() }).then(r => r.json()).catch(() => null),
      fetch(`${MIDDLEWARE_URL}/tracabilite/alerts?societe_id=`, { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/certificate/status`,       { headers: authH() }).then(r => r.json()).catch(() => null),
      fetch(`${MIDDLEWARE_URL}/companies`,                { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/factures?exercice=${parseInt(localStorage.getItem("bridge_exercice")) || new Date().getFullYear()}`, { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/clients`,                  { headers: authH() }).then(r => r.ok ? r.json() : []).catch(() => []),   // n5aliwha kif mahiya
      fetch(`${MIDDLEWARE_URL}/archive/invoices`,         { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/archive/statistics`,       { headers: authH() }).then(r => r.json()).catch(() => null),
      fetch(`${MIDDLEWARE_URL}/tracabilite/dashboard`, { headers: authH() }).then(r => r.json()).catch(() => null),
      fetch(`${MIDDLEWARE_URL}/avoirs`,                   { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/offline/queue`,            { headers: authH() }).then(r => r.json()).catch(() => []),
      fetch(`${MIDDLEWARE_URL}/ance/sandbox/results`,     { headers: authH() }).then(r => r.json()).catch(() => null),
    ]);

    setGlobalStats(stats?.statistics || stats);
    setAlerts(Array.isArray(al) ? al : al?.alerts || []);
    setCertStatus(cert);

    // Companies
    const companiesData = Array.isArray(comp) ? comp : comp?.companies || [];
    setCompanies(companiesData);

    // Dashboard stats globales
    const dashResults = await Promise.all(
      companiesData.map(c => {
        const sid = c.id;
        return fetch(`${MIDDLEWARE_URL}/dashboard/data?societe_id=${sid}`, { headers: authH() })
          .then(r => r.json()).catch(() => null);
      })
    );

    const totalStats = dashResults.reduce((acc, d) => {
      if (!d?.stats) return acc;
      return {
        total:        (acc.total        || 0) + (d.stats.total        || 0),
        today:        (acc.today        || 0) + (d.stats.today        || 0),
        last_30_days: (acc.last_30_days || 0) + (d.stats.last_30_days || 0),
        ACCEPTED:     (acc.ACCEPTED     || 0) + (d.stats.by_status?.ACCEPTED || 0),
        CREDITED:     (acc.CREDITED     || 0) + (d.stats.by_status?.CREDITED || 0),
      };
    }, {});

    setDashboardData({
      stats: {
        ...totalStats,
        by_status: { ACCEPTED: totalStats.ACCEPTED || 0, CREDITED: totalStats.CREDITED || 0 }
      }
    });

    setFactures(Array.isArray(fact) ? fact : fact?.factures || []);
    
    // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
    // IMPORTANT : On vide les clients ici
    setClients([]);   
    // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

    setArchive(Array.isArray(arch) ? arch : arch?.invoices || []);
    setArchiveStats(archSt?.statistics || archSt);
    setTracabilite(trace);
    setAvoirs(Array.isArray(av) ? av : av?.avoirs || []);
    setOfflineQueue(Array.isArray(off) ? off : off?.queue || []);
    setSandboxRes(sb);

  } catch (err) {
    console.error("Erreur dans fetchAllMiddleware:", err);
    showToast("Erreur middleware", "error");
  } finally {
    setMiddleLoading(false);
  }
};
  // ─── CHANGE ROLE ──────────────────────────────────────────
  const handleChangeRole = async () => {
    if (!roleModal || !selectedRole) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/admin/users/${roleModal.id}/role`, {
        method: "PUT", headers: authH(), body: JSON.stringify({ role: selectedRole }),
      });
      if (res.ok) { showToast("✅ Rôle mis à jour !"); setRoleModal(null); fetchUsers(); }
      else showToast("Erreur changement de rôle", "error");
    } catch { showToast("Erreur réseau", "error"); }
    setActionLoading(false);
  };

  // ─── DELETE USER ──────────────────────────────────────────
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/admin/users/${confirmDelete.id}`, {
        method: "DELETE", headers: authH(),
      });
      if (res.ok) { showToast("✅ Utilisateur supprimé !"); setConfirmDelete(null); fetchUsers(); }
      else showToast("Erreur suppression", "error");
    } catch { showToast("Erreur réseau", "error"); }
    setActionLoading(false);
  };

  // ─── CRUD SOCIÉTÉS ────────────────────────────────────────
  const openCreateSoc = () => {
    setEditSoc(null);
    setSocForm({ name: "", tax_id: "", address: "", phone: "", email: "" });
    setSocConfirm(false);
    setShowSocModal(true);
  };

  const openEditSoc = (soc) => {
    setEditSoc(soc);
    setSocForm({ name: soc.name || soc.nom || "", tax_id: soc.tax_id || soc.matricule_fiscale || "", address: soc.address || soc.adresse || "", phone: soc.phone || soc.telephone || "", email: soc.email || "" });
    setSocConfirm(false);
    setShowSocModal(true);
  };

  const handleSaveSoc = async () => {
  if (!socForm.name || !socForm.tax_id) {
    showToast("Nom et Matricule fiscal obligatoires", "error"); return;
  }
  setSocLoading(true);
  try {
    const params = new URLSearchParams();
    if (socForm.name)    params.append("name",    socForm.name);
    if (socForm.tax_id)  params.append("tax_id",  socForm.tax_id);
    if (socForm.address) params.append("address", socForm.address);
    if (socForm.phone)   params.append("phone",   socForm.phone);
    if (socForm.email)   params.append("email",   socForm.email);

    const url    = editSoc
      ? `${MIDDLEWARE_URL}/companies/${editSoc.id}?${params.toString()}`
      : `${MIDDLEWARE_URL}/companies?${params.toString()}`;
    const method = editSoc ? "PUT" : "POST";

    const res  = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token()}` },
    });

    // Middleware yrajje3 JSON wella text
    const text = await res.text();
    let data = {};
    try { data = JSON.parse(text); } catch {}

    if (res.ok || data.success) {
      showToast(editSoc ? "✅ Société modifiée !" : "✅ Société créée !");
      setShowSocModal(false);
      setSocConfirm(false);
      fetchAllMiddleware();
    } else {
      showToast(data.detail || data.message || `Erreur ${res.status}`, "error");
    }
  } catch (e) {
    showToast("Erreur réseau — " + e.message, "error");
  }
  setSocLoading(false);
};

const handleActivate = async (userId) => {
  try {
    const res  = await fetch(`${BACKEND_URL}/api/auth/admin/activate/${userId}`, { method: "PUT", headers: authH() });
    const data = await res.json();
    if (res.ok) { showToast("✅ Compte activé !"); fetchSocietes(); }
    else showToast(data.error || "Erreur activation", "error");
  } catch { showToast("Erreur réseau", "error"); }
};

  const handleDeleteSoc = async () => {
    if (!confirmDelSoc) return;
    setSocLoading(true);
    try {
      const res = await fetch(`${MIDDLEWARE_URL}/companies/${confirmDelSoc.id}`, { method: "DELETE", headers: authH() });
      if (res.ok) { showToast("✅ Société supprimée !"); setConfirmDelSoc(null); fetchAllMiddleware(); }
      else showToast("Erreur suppression société", "error");
    } catch { showToast("Erreur réseau", "error"); }
    setSocLoading(false);
  };

  const fetchSocietes = async () => {
  setSocLoading(true);
  try {
    // ✅ Sociétés plateforme (toutes les 79)
    const res = await fetch(`${BACKEND_URL}/api/auth/societes`, { headers: authH() });
    const data = await res.json();
    setAllPlatformSocietes(Array.isArray(data) ? data : data.societes || []);

    // ✅ Inscriptions en attente (inchangé)
    const res2 = await fetch(`${BACKEND_URL}/api/auth/pending`, { headers: authH() });
    const data2 = await res2.json();
    setPendingSocietes(Array.isArray(data2) ? data2 : data2.users || []);
  } catch { }
  setSocLoading(false);
};
const fetchModRequests = async () => {
  setModReqLoading(true);
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/modification-requests?status=pending`, { headers: authH() });
    const data = await res.json();
    setModRequests(data.requests || []);
  } catch (err) {
    showToast("Erreur chargement demandes", "error");
  } finally {
    setModReqLoading(false);
  }
};

const syncSocietes = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/societes/sync`, {
      method: "POST",
      headers: authH()
    });
    const data = await res.json();
    if (data.success) {
      console.log(`✅ ${data.synced} sociétés synchronisées`);
      fetchSocietes();
    }
  } catch (err) {
    console.error("Sync error:", err);
  }
};

const fetchAssignments = async () => {
  try {
    const res  = await fetch(`${BACKEND_URL}/api/auth/assignments`, { 
      headers: authH() 
    });
    const data = await res.json();
    setAssignments(data.assignments || []);
  } catch { showToast("Erreur chargement assignments", "error"); }
};

const fetchAdmins = async () => {
  try {
    const res  = await fetch(`${BACKEND_URL}/api/auth/admin/users`, { 
      headers: authH() 
    });
    const data = await res.json();
    const adminList = (Array.isArray(data) ? data : data.users || [])
      .filter(u => u.role === "admin");
    setAdmins(adminList);
  } catch {}
};
const fetchTracabilite = async (societeId = null) => {
  setMiddleLoading(true);
  try {
    const res = await fetch(`${MIDDLEWARE_URL}/tracabilite/dashboard`, { headers: authH() });
    const data = await res.json();

if (societeId && data?.recent_changes) {
  const societe = companies.find(c => c.id === societeId);
  const nom = (societe?.name || societe?.nom || "").toLowerCase();
  const filtered = data.recent_changes.filter(r =>
    String(r.societe_id) === String(societeId) ||
    String(r.invoice || "").toLowerCase().includes(nom) ||
    String(r.societe_nom || "").toLowerCase().includes(nom)
  );
      const by_status = filtered.reduce((acc, r) => {
        acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
      }, {});
      setTracabilite({ ...data, by_status, recent_changes: filtered });
    } else {
      setTracabilite(data);
    }

    const societe = companies.find(c => c.id === societeId);
    if (societeId) showToast(`✅ Tracabilité — ${societe?.name || societe?.nom}`, "success");

  } catch {
    showToast("Erreur chargement tracabilité", "error");
  }
  setMiddleLoading(false);
};
const handleCreateAssignment = async () => {
  if (!assignForm.admin_id || !assignForm.societe_id) {
    showToast("Admin et Société obligatoires", "error"); 
    return;
  }
  setAssignLoading(true);
  setAssignResult(null);
  try {
    const res  = await fetch(`${BACKEND_URL}/api/auth/assignments`, {
      method:  "POST",
      headers: authH(),
      body:    JSON.stringify({
        admin_id:    parseInt(assignForm.admin_id),
        societe_id:  parseInt(assignForm.societe_id),
        commentaire: assignForm.commentaire,
        factures_ids: assignForm.factures_ids
      })
    });
    const data = await res.json();
    if (data.success) {
      showToast(`✅ ${data.message}`);
      setAssignResult({ success: true, message: data.message });
      setAssignForm({ admin_id: "", societe_id: "", commentaire: "", factures_ids: [] });
      fetchAssignments();
    } else {
      showToast(data.error || "Erreur", "error");
      setAssignResult({ success: false, message: data.error });
    }
  } catch { showToast("Erreur réseau", "error"); }
  setAssignLoading(false);
};

const handleModAction = async (id, action, reason = "") => {
  setModActionLoading(prev => ({ ...prev, [id]: true }));
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/modification-requests/${id}/${action}`, {
      method: "PUT",
      headers: authH(),
      body: JSON.stringify({ reason })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Erreur traitement");
    showToast(action === "approve" ? "✅ Modification approuvée" : "❌ Demande rejetée");
    fetchModRequests();
    fetchUsers(); // Refresh users list
  } catch (err) {
    showToast("❌ " + err.message, "error");
  } finally {
    setModActionLoading(prev => ({ ...prev, [id]: false }));
  }
};

  // ─── STATS PAR SOCIÉTÉ ────────────────────────────────────
  const fetchSocStats = async (soc) => {
  setSelectedSocStat(soc);
  setSocStatsLoading(true);
  setSocStats(null);
  try {
    const sid = soc.id;
    const societeName = (soc.name || soc.nom || "").trim();
    const BRIDGE_URL = process.env.REACT_APP_BRIDGE_URL;

   const [fact, av] = await Promise.all([
  fetch(`${BRIDGE_URL}/factures?societe_nom=${encodeURIComponent(societeName)}&exercice=${parseInt(localStorage.getItem("bridge_exercice")) || new Date().getFullYear()}`,
    { headers: authH() }).then(r => r.json()).catch(() => []),
  fetch(`${MIDDLEWARE_URL}/avoirs?societe_id=${sid}`,
    { headers: authH() }).then(r => r.json()).catch(() => []),
]);

    const factList  = Array.isArray(fact) ? fact : (fact.factures || fact.data || []);
    const avoirList = Array.isArray(av)   ? av   : (av.avoirs    || []);

    console.log("Facture sample:", factList[0]);
console.log("Statut field:", factList[0]?.StatutCommande, factList[0]?.Statut, factList[0]?.status);


    const archived = factList.filter(f =>
      (f.StatutCommande || f.Statut || f.status || "").toUpperCase() === "ARCHIVED"
    ).length;

    const accepted = factList.filter(f =>
      ["ACCEPTED", "ARCHIVED"].includes((f.StatutCommande || f.Statut || f.status || "").toUpperCase())
    ).length;

    setSocStats({
      archive: {
        total: archived,
        by_status: { ACCEPTED: accepted },
      },
      factures: factList,
      avoirs:   avoirList,
    });

  } catch { showToast("Erreur stats société", "error"); }
  setSocStatsLoading(false);
};

const openEditSocAdmin = (soc) => {
  setEditSocAdmin(soc);
  setEditStepAdmin(1);
  setEditFormAdmin({
    nom:       soc.nom || soc.name || "",
    adresse:   soc.adresse || soc.address || "",
    telephone: soc.telephone || soc.phone || "",
    email:     soc.email || "",
    matricule: soc.matricule_fiscal || soc.tax_id || "",
  });
};

const handleSaveEditSocAdmin = async () => {
  if (!editSocAdmin) return;
  setSocLoading(true);
  try {
    const res = await fetch(`${MIDDLEWARE_URL}/companies/${editSocAdmin.id}`, {
      method: "PUT",
      headers: authH(),
      body: JSON.stringify({
        name:    editFormAdmin.nom,
        address: editFormAdmin.adresse,
        phone:   editFormAdmin.telephone,
        email:   editFormAdmin.email,
        tax_id:  editFormAdmin.matricule,
      }),
    });
    if (res.ok) {
      showToast("✅ Société modifiée avec succès !");
      setEditSocAdmin(null);
      setEditStepAdmin(1);
      fetchSocietes();
      fetchAllMiddleware();
    } else {
      const d = await res.json();
      showToast(d.detail || "Erreur modification", "error");
    }
  } catch { showToast("Erreur réseau", "error"); }
  setSocLoading(false);
};

const handleDeleteSocAdmin = async (socId, socNom) => {
  if (!window.confirm(`Confirmer la suppression de "${socNom}" ?`)) return;
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/societes/${socId}`, {
      method: "DELETE", headers: authH()
    });
    if (res.ok) {
      showToast(`✅ "${socNom}" supprimée`);
      fetchSocietes();
      fetchAllMiddleware();
    } else {
      const d = await res.json();
      showToast(d.error || "Erreur suppression", "error");
    }
  } catch { showToast("Erreur réseau", "error"); }
};

  // ─── DSS SUBMIT ───────────────────────────────────────────
  const handleDssSubmit = async () => {
    if (!dssNumFacture) { showToast("Entrez un numéro de facture", "error"); return; }
    setDssLoading(true); setDssResult(null);
    try {
      const res  = await fetch(`${MIDDLEWARE_URL}/factures/${dssNumFacture}/submit-dss`, { method: "POST", headers: authH() });
      const data = await res.json();
      setDssResult(data);
      if (data.success) {
  showToast(`✅ Facture ${dssNumFacture} envoyée à ANCE !`);
  handlePublishEvent(dssNumFacture, "INVOICE_CREATED");
}
      else showToast(data.detail || "Erreur DSS", "error");
    } catch { showToast("Erreur réseau DSS", "error"); }
    setDssLoading(false);
  };

  // ─── SANDBOX SUBMIT ───────────────────────────────────────
  const handleSandboxSubmit = async () => {
    if (!sbNumFacture) { showToast("Entrez un numéro de facture", "error"); return; }
    setSbLoading(true);
    try {
      const cleanNum = sbNumFacture.toString().replace(/[^0-9]/g, "");
const res = await fetch(`${MIDDLEWARE_URL}/ance/sandbox/submit/${cleanNum}?societe_id=${sbSociete?.id || ""}`, { method: "POST", headers: authH() });
      const data = await res.json();
      if (data.success) { 
  showToast(`✅ Sandbox OK — UUID: ${data.uuid_fiscal}`); 
  handlePublishEvent(sbNumFacture, "INVOICE_CREATED");
  fetchAllMiddleware(); 
}
      else showToast(data.detail || "Erreur sandbox", "error");
    } catch { showToast("Erreur réseau sandbox", "error"); }
    setSbLoading(false);
  };

  const handlePublishEvent = async (numFact = null, evType = null) => {
  const num = numFact || eventNumFacture;
  const type = evType || eventType;
  if (!num) { if (!numFact) showToast("Entrez un numéro de facture", "error"); return; }
  if (!evType) setEventLoading(true);
  try {
    const res = await fetch(
      `${MIDDLEWARE_URL}/events/publish?num_facture=${num}&event_type=${type}`,
      { method: "POST", headers: authH() }
    );
const data = await res.json();

    //  FIX : on ajoute l'event dans l'historique que ce soit RabbitMQ ou simulé
    // Avant : setEvents appelé seulement si data.published = true
    // Problème : en mode simulation published = false → historique restait vide
    setEvents(prev => [{
      id:        Date.now(),
      numFacture: num,
      eventType:  type,
      published:  data.published,
      message:    data.message,
      timestamp:  new Date().toLocaleTimeString(),
      auto:       !!numFact
    }, ...prev].slice(0, 50));

    // Toast différent selon RabbitMQ réel ou simulation
    if (data.published) {
      if (!evType) showToast(`✅ Event ${type} publié — Facture #${num}`);
    } else {
      if (!evType) showToast(`~ Simulé — ${data.message || "RabbitMQ non disponible"}`, "warning");
    }

  } catch { if (!evType) showToast("Erreur réseau event bus", "error"); }
  if (!evType) setEventLoading(false);
};

  // ─── COMPUTED ─────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return (!q || u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q)) &&
           (filterRole === "all" || u.role === filterRole);
  });

  const certOk      = certStatus?.valid || certStatus?.status === "valid";
  const currentUser = getCookie("name") || "Super Admin";
  const initials    = currentUser.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const handleNavClick = (item) => {
    if (item.tab)  setActiveTab(item.tab);
    else if (item.path) navigate(item.path);
  };

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div style={S.root}>
      <style>{css}</style>

      {/* SIDEBAR */}
      <aside className={`sidebar-responsive ${mobileMenuOpen ? "open" : ""}`} style={{ ...S.sidebar, width: sidebarCollapsed ? 60 : 230 }}>
        <div style={S.brand} onClick={() => navigate("/home")}>
          <div style={S.brandMark}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 1L16.5 5.5V12.5L9 17L1.5 12.5V5.5L9 1Z" fill="white" opacity="0.9"/>
              <path d="M9 5L13 7.5V12.5L9 15L5 12.5V7.5L9 5Z" fill="white" opacity="0.4"/>
              boxShadow: "4px 0 20px rgba(124,58,237,0.06)"
            </svg>
          </div>
          {!sidebarCollapsed && <span style={S.brandName}>LOGIMES</span>}
        </div>

        {!sidebarCollapsed && (
          <div style={S.godBadge}>◆ GOD MODE</div>
        )}

        <button style={S.collapseBtn} onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
          {sidebarCollapsed ? "→" : "←"}
        </button>

        <nav style={S.sidebarNav}>
          <div style={{ ...S.navItem, color: "#64748B" }} className="nav-hover" onClick={() => navigate("/home")}>
            <span style={S.navIcon}>🏠</span>
            {!sidebarCollapsed && <span style={S.navLabel}>Accueil</span>}
          </div>

          {NAV_SECTIONS.map((sec) => (
            <div key={sec.label}>
              {!sidebarCollapsed && (
                <div style={{ ...S.sectionLabel, color: sec.color }}>
                  <span style={{ ...S.sectionDot, background: sec.color }} />
                  {sec.label}
                </div>
              )}
              {sidebarCollapsed && <div style={S.sectionDivider} />}
              {sec.items.map((item) => {
                const isActive = item.tab ? activeTab === item.tab : location.pathname === item.path;
                return (
                  <div key={item.label}
                    style={{ ...S.navItem, background: isActive ? sec.color + "18" : "transparent", color: isActive ? sec.color : "#64748B", borderLeft: isActive ? `3px solid ${sec.color}` : "3px solid transparent" }}
                    className={isActive ? "" : "nav-hover"}
                    onClick={() => handleNavClick(item)}
                    title={sidebarCollapsed ? item.label : ""}>
                    <span style={S.navIcon}>{item.icon}</span>
                    {!sidebarCollapsed && <span style={S.navLabel}>{item.label}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={S.sidebarFooter}>
          {!sidebarCollapsed && (
            <div style={S.userCard}>
              <div style={S.userInitials}>{initials}</div>
              <div style={S.userMeta}>
                <div style={S.userNameText}>{currentUser}</div>
                <div style={S.userRoleTag}>◆ Super Admin</div>
              </div>
            </div>
          )}
          <button style={S.logoutBtn} className="logout-hover" title="Déconnexion"
            onClick={() => {
              ["token","role","name","societe_id","societe_folder"].forEach(k =>
                document.cookie = `${k}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
              );
              navigate("/login");
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {!sidebarCollapsed && "Déconnexion"}
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="main-responsive" style={{ ...S.main, marginLeft: sidebarCollapsed ? 60 : 230 }}>

        {/* HEADER */}
        <header className="header-responsive" style={S.header}>
          <button
            className="mobile-menu-btn"
            style={{ display: "none", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", marginRight: 10, fontSize: "1.1rem" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            ☰
          </button>
          <div>
            <div style={S.headerEyebrow}>Administration · Accès restreint · God Mode</div>
            <h1 style={S.headerTitle}>Super Admin Dashboard</h1>
          </div>
          <div style={S.headerActions}>
            {alerts.length > 0 && (
              <span style={{ ...S.pill, ...S.pillRed, cursor: "pointer" }} onClick={() => setActiveTab("alertes")}>
                🔔 {alerts.length} alerte{alerts.length > 1 ? "s" : ""}
              </span>
            )}
            {pendingSocietes.length > 0 && (
              <span style={{ ...S.pill, background: "#fef3c7", color: "#d97706", cursor: "pointer" }}
                onClick={() => setActiveTab("societes")}>
                ⏳ {pendingSocietes.length} inscription{pendingSocietes.length > 1 ? "s" : ""} en attente
              </span>
            )}
            {certStatus && (
              <span style={{ ...S.pill, ...(certOk ? S.pillGreen : S.pillRed) }}>
                {certOk ? "🔒 Cert. valide" : "⚠️ Cert. expiré"}
              </span>
            )}
            <button style={S.btnSecondary} className="btn-hover" onClick={() => { fetchUsers(); fetchAllMiddleware(); }}>↻ Actualiser</button>
            <button style={S.btnGhost} className="btn-hover" onClick={() => setActiveTab("dashboard")}>← Vue d'ensemble</button>
          </div>
        </header>

        {/* TABS */}
        <div className="tabsbar-responsive" style={S.tabsBar}>
          {TABS.map(t => (
            <button key={t.id}
              style={{ ...S.tabBtn, ...(activeTab === t.id ? S.tabBtnActive : {}) }}
              onClick={() => setActiveTab(t.id)}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.id === "alertes" && alerts.length > 0 && <span style={S.tabBadge}>{alerts.length}</span>}
            </button>
          ))}
        </div>

        <div className="content-responsive" style={S.content}>

          {/* ══ TAB: VUE D'ENSEMBLE ══ */}
          {activeTab === "dashboard" && (
            <div style={S.tabContent}>
              <div style={S.sectionTitle}>👥 Utilisateurs</div>
              <div className="statsgrid-responsive" style={S.statsGrid4}>
                {[
                  { label: "Total Users",   value: userStats.total,       color: "#7C3AED", sub: "comptes actifs"  },
                  { label: "Super Admins",  value: userStats.super_admin, color: "#7C3AED", sub: "accès complet"   },
                  { label: "Admins",        value: userStats.admin,       color: "#0EA5E9", sub: "accès avancé"    },
                  { label: "Utilisateurs",  value: userStats.user,        color: "#64748B", sub: "accès standard"  },
                ].map((s, i) => <StatCard key={i} {...s} />)}
              </div>

              <div style={S.sectionTitle}>📊 Facturation Globale</div>
              <div className="statsgrid-responsive" style={S.statsGrid4}>
                {[
                  { label: "Factures Total",    value: middleLoading ? "..." : (dashboardData?.stats?.total        ?? factures.length), color: "#6c63ff", sub: "toutes sociétés"   },
                  { label: "Aujourd'hui",       value: middleLoading ? "..." : (dashboardData?.stats?.today        ?? "—"),             color: "#7C3AED", sub: "créées aujourd'hui" },
                  { label: "30 derniers jours", value: middleLoading ? "..." : (dashboardData?.stats?.last_30_days ?? "—"),             color: "#0EA5E9", sub: "ce mois-ci"         },
                  { label: "Acceptées ANCE",    value: middleLoading ? "..." : (dashboardData?.stats?.by_status?.ACCEPTED ?? "—"),      color: "#43b89c", sub: "validées"           },
                ].map((s, i) => <StatCard key={i} {...s} />)}
              </div>

              {alerts.length > 0 && (
                <>
                  <div style={S.sectionTitle}>🔔 Alertes récentes</div>
                  <div style={S.listCard}>
                    {alerts.slice(0, 5).map((a, i) => (
                      <div key={i} style={S.listRow}>
                        <span style={S.alertDot} />
                        <span style={{ flex: 1, fontSize: "0.83rem", color: "#334155" }}>{a.message || a.description || a.type || "Alerte"}</span>
                        <span style={S.dateCell}>{a.date || a.created_at || ""}</span>
                      </div>
                    ))}
                    {alerts.length > 5 && <div style={S.moreLink} onClick={() => setActiveTab("alertes")}>Voir toutes →</div>}
                  </div>
                </>
              )}

              <div style={S.sectionTitle}>⚡ Accès rapide</div>
              <div className="quickgrid-responsive" style={S.quickGrid}>
                {[
                  { icon: "👥", label: "Gérer Users",       color: "#7C3AED", action: () => setActiveTab("users")      },
                  { icon: "🏢", label: "Sociétés",          color: "#e11d48", action: () => setActiveTab("societes")   },
                  { icon: "📊", label: "Stats/Société",     color: "#f59e0b", action: () => setActiveTab("stats")      },
                  { icon: "🗄️", label: "Archive ANCE",     color: "#43b89c", action: () => setActiveTab("archive")    },
                  { icon: "🔍", label: "Tracabilité",       color: "#5c8df6", action: () => setActiveTab("tracabilite")},
                  { icon: "🧾", label: "Toutes Factures",   color: "#6c63ff", action: () => setActiveTab("factures")   },
                  { icon: "🧪", label: "Sandbox ANCE",      color: "#e11d48", action: () => setActiveTab("sandbox")    },
                  { icon: "⚙️", label: "Système",           color: "#9b59b6", action: () => setActiveTab("systeme")    },
                  { icon: "✅", label: "Rapport Tests", color: "#16a34a", action: () => setActiveTab("tests") },
                ].map((q, i) => (
                  <div key={i} style={{ ...S.quickCard, borderColor: q.color + "30" }} className="stat-hover" onClick={q.action}>
                    <span style={{ ...S.quickIcon, background: q.color + "15", color: q.color }}>{q.icon}</span>
                    <span style={S.quickLabel}>{q.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ TAB: USERS & RÔLES ══ */}
          {activeTab === "users" && (
            <div style={S.tabContent}>
              <div className="statsgrid-responsive" style={S.statsGrid4}>
                {[
                  { label: "Total",        value: userStats.total,       color: "#7C3AED", sub: "comptes actifs" },
                  { label: "Super Admins", value: userStats.super_admin, color: "#7C3AED", sub: "accès complet"  },
                  { label: "Admins",       value: userStats.admin,       color: "#0EA5E9", sub: "accès avancé"   },
                  { label: "Utilisateurs", value: userStats.user,        color: "#64748B", sub: "accès standard" },
                ].map((s, i) => <StatCard key={i} {...s} />)}
              </div>

              <div style={S.toolbar}>
                <div style={S.searchBox}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input style={S.searchInput} placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
                  {search && <button style={S.searchClear} onClick={() => setSearch("")}>×</button>}
                </div>
                <div style={S.filterGroup}>
                  {["all","super_admin","admin","user"].map(r => (
                    <button key={r}
                      style={{ ...S.filterChip, ...(filterRole === r ? S.filterChipActive : {}) }}
                      className={filterRole === r ? "" : "chip-hover"}
                      onClick={() => setFilterRole(r)}>
                      {r === "all" ? "Tous" : ROLE_CONFIG[r].label}
                    </button>
                  ))}
                </div>
                <button style={S.btnSecondary} className="btn-hover" onClick={fetchUsers}>↻</button>
              </div>

              <div style={S.resultMeta}>
                <span style={S.resultCount}>{filteredUsers.length} résultat{filteredUsers.length !== 1 ? "s" : ""}</span>
              </div>

              {usersLoading ? <StateBox loading /> : filteredUsers.length === 0 ? <StateBox empty /> : (
                <div style={S.tableCard}>
                  <table style={S.table}>
                    <thead>
                      <tr>{["#","Utilisateur","Email","Rôle","Depuis","Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => {
                        const rc     = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                        const isSelf = u.email === getCookie("email");
                        return (
                          <tr key={u.id} style={S.tr}
                            onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}>
                            <td style={S.td}><span style={S.idNum}>{u.id}</span></td>
                            <td style={S.td}>
                              <div style={S.userCell}>
                                <div style={{ ...S.avatar, background: rc.color }}>
                                  {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={S.userName3}>{u.name || "—"}</div>
                                  {isSelf && <span style={S.youBadge}>Vous</span>}
                                </div>
                              </div>
                            </td>
                            <td style={S.td}><span style={S.emailCell}>{u.email}</span></td>
                            <td style={S.td}>
                              <span style={{ ...S.roleChip, color: rc.color, background: rc.bg, borderColor: rc.border }}>
                                {rc.icon} {rc.label}
                              </span>
                            </td>
                            <td style={S.td}><span style={S.dateCell}>{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</span></td>
                            <td style={S.td}>
                              <div style={S.actionsRow}>
                                <button style={S.actionEdit} className="btn-hover"
                                  onClick={() => { setRoleModal(u); setSelectedRole(u.role); }}>
                                  Modifier rôle
                                </button>
                                {!isSelf && (
                                  <button style={S.actionDelete} className="btn-hover" onClick={() => setConfirmDelete(u)}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3 6 5 6 21 6"/>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══ TAB: SOCIÉTÉS CRUD ══ */}
          {activeTab === "societes" && (
            <div style={S.tabContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={S.sectionTitle}>🏢 Gestion des Sociétés</div>
                <button style={S.btnPrimary} onClick={openCreateSoc}>➕ Nouvelle Société</button>
              </div>

              {/* Inscriptions en attente */}
              <div style={S.sectionTitle}>
                ⏳ Inscriptions en attente ({pendingSocietes.length})
              </div>
              {socLoading ? <StateBox loading /> : pendingSocietes.length === 0 ? (
                <div style={{ ...S.emptyState, padding: "24px" }}>
                  <div style={{ fontSize: "1.5rem" }}>✅</div>
                  <p style={{ fontSize: "0.82rem", color: "#94A3B8" }}>Aucune inscription en attente</p>
                </div>
              ) : (
                <div style={S.tableCard}>
                  <table style={S.table}>
                    <thead>
                      <tr>{["#","Nom","Email","Date inscription","Action"].map((h, i) => (
                      <th key={h} style={i === 4 ? { ...S.th, position: "sticky", right: 0, background: "#FAFAFA", zIndex: 2, boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.08)" } : S.th}>{h}</th>
                    ))}</tr>
                    </thead>
                    <tbody>
                      {pendingSocietes.map((u, i) => (
                        <tr key={u.id || i} style={S.tr}
                          onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                          onMouseLeave={e => e.currentTarget.style.background = ""}>
                          <td style={S.td}><span style={S.idNum}>{i + 1}</span></td>
                          <td style={S.td}><span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{u.name || u.nom || "—"}</span></td>
                          <td style={S.td}><span style={S.emailCell}>{u.email || "—"}</span></td>
                          <td style={S.td}><span style={S.dateCell}>{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</span></td>
                          <td style={{ ...S.td, position: "sticky", right: 0, background: "#fff", zIndex: 1, boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.08)" }}>
  <button style={{ ...S.actionEdit, color: "#16a34a", borderColor: "#16a34a", fontWeight: 700 }}
    className="btn-hover" onClick={() => handleActivate(u.id)}>
    ✓ Valider
  </button>
</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            {/* ── Sociétés enregistrées (depuis Admin.js) ── */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={S.sectionTitle}>🏢 Sociétés enregistrées ({allPlatformSocietes.length})</div>
              <button style={S.btnSecondary} className="btn-hover" onClick={fetchSocietes}>↻ Actualiser</button>
            </div>

            {allPlatformSocietes.length === 0 ? (
              <div style={S.emptyState}>
                <div style={{ fontSize:"2rem" }}>🏢</div>
                <p style={{ fontSize:"0.84rem", color:"#94A3B8" }}>Aucune société trouvée</p>
              </div>
            ) : (
              <div style={S.tableCard}>
                <table style={S.table}>
                  <thead>
<tr>{["#","Nom","Matricule Fiscal","Adresse","Contact","Actions"].map((h, i) => (
  <th key={h} style={i === 5 ? { ...S.th, position: "sticky", right: 0, background: "#FAFAFA", zIndex: 2, boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.08)" } : S.th}>{h}</th>
))}</tr>                  </thead>
                  <tbody>
                    {(socShowAll ? allPlatformSocietes : allPlatformSocietes.slice(0, 4)).map((soc, i) => (
                      <tr key={soc.id || i} style={S.tr}
                        onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td style={S.td}><span style={S.idNum}>{i+1}</span></td>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:32, height:32, borderRadius:8, background:"#F5F3FF",
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:"0.85rem", fontWeight:800, color:"#7C3AED" }}>
                              {(soc.nom || soc.name || "S").charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontWeight:700, fontSize:"0.85rem", color:"#0F172A" }}>
                              {soc.nom || soc.name || "—"}
                            </span>
                          </div>
                        </td>
                        <td style={S.td}>
                          <span style={{ fontSize:"0.75rem", fontFamily:"monospace", color:"#64748B" }}>
                            {soc.matricule_fiscal || soc.tax_id || "—"}
                          </span>
                        </td>
                        <td style={S.td}><span style={S.emailCell}>{soc.adresse || soc.address || "—"}</span></td>
                        <td style={S.td}><span style={S.emailCell}>{soc.email || soc.telephone || "—"}</span></td>
                        <td style={{ ...S.td, position: "sticky", right: 0, background: "#fff", zIndex: 1, boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.08)" }}>
                          <div style={{ display:"flex", gap:6 }}>
                            <button style={{ ...S.actionEdit, color:"#f59e0b", borderColor:"#f59e0b" }}
                              className="btn-hover"
                              onClick={() => openEditSocAdmin(soc)}>
                              ✏️ Modifier
                            </button>
                            <button style={S.actionDelete} className="btn-hover"
                              onClick={() => handleDeleteSocAdmin(soc.id, soc.nom || soc.name)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allPlatformSocietes.length > 4 && (
              <div style={{ textAlign:"center", padding:"12px" }}>
                <button 
                  style={{ ...S.btnSecondary, fontSize:"0.78rem" }} 
                  className="btn-hover"
                  onClick={() => setSocShowAll(!socShowAll)}>
                  {socShowAll 
                    ? "▲ Réduire" 
                    : `▼ Voir tout (${allPlatformSocietes.length})`}
                </button>
              </div>
            )}
              </div>
            )}

            {/* Modal modifier société (2 étapes) */}
            {editSocAdmin && (
              <div style={S.overlay} onClick={() => { setEditSocAdmin(null); setEditStepAdmin(1); }}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                  <div style={S.modalHead}>
                    <div>
                      <div style={{ ...S.modalEyebrow, color:"#f59e0b" }}>
                        Modifier infos · Étape {editStepAdmin}/2
                      </div>
                      <h3 style={S.modalTitle}>{editSocAdmin.nom || editSocAdmin.name}</h3>
                    </div>
                    <button style={S.modalX} onClick={() => { setEditSocAdmin(null); setEditStepAdmin(1); }}>×</button>
                  </div>

                  {/* Stepper */}
                  <div style={{ display:"flex", alignItems:"center", gap:0, padding:"0 24px 16px", borderBottom:"1px solid #F1F5F9", marginTop:16 }}>
                    {[{n:1,label:"Saisir modifications"},{n:2,label:"Valider & Confirmer"}].map((step, si) => (
                      <React.Fragment key={si}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:26, height:26, borderRadius:"50%",
                            background: editStepAdmin >= step.n ? "#7C3AED" : "#E2E8F0",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:"0.72rem", fontWeight:800,
                            color: editStepAdmin >= step.n ? "#fff" : "#94A3B8" }}>
                            {editStepAdmin > step.n ? "✓" : step.n}
                          </div>
                          <span style={{ fontSize:"0.75rem", fontWeight:600,
                            color: editStepAdmin >= step.n ? "#0F172A" : "#94A3B8" }}>
                            {step.label}
                          </span>
                        </div>
                        {si === 0 && (
                          <div style={{ flex:1, height:2,
                            background: editStepAdmin > 1 ? "#7C3AED" : "#E2E8F0",
                            margin:"0 12px" }} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <div style={S.modalBody}>
                    {editStepAdmin === 1 ? (
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        <div style={{ padding:"12px 16px", background:"#fef3c7", borderRadius:9,
                          border:"1px solid #fde68a", fontSize:"0.78rem", color:"#92400e" }}>
                          ⚠️ Modification des informations sensibles — sera enregistrée dans le journal d'audit.
                        </div>
                        {[
                          { label:"Nom",             key:"nom",       ph:"Ex: TPM SARL",        sensitive:true  },
                          { label:"Matricule fiscal",key:"matricule", ph:"Ex: 1234567A",         sensitive:true  },
                          { label:"Adresse",         key:"adresse",   ph:"Ex: 12 rue Tunis",    sensitive:false },
                          { label:"Téléphone",       key:"telephone", ph:"Ex: +216 71 000 000", sensitive:false },
                          { label:"Email",           key:"email",     ph:"Ex: contact@soc.tn",  sensitive:false },
                        ].map(f => (
                          <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:5 }}>
                            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B",
                              display:"flex", alignItems:"center", gap:6 }}>
                              {f.label}
                              {f.sensitive && (
                                <span style={{ background:"#fff0f0", color:"#dc2626", borderRadius:4,
                                  padding:"1px 6px", fontSize:"0.6rem", fontWeight:700 }}>Sensible</span>
                              )}
                            </label>
                            <input
                              style={{ ...S.input, border:`1.5px solid ${f.sensitive ? "#fecaca" : "#E2E8F0"}` }}
                              placeholder={f.ph}
                              value={editFormAdmin[f.key] || ""}
                              onChange={e => setEditFormAdmin(prev => ({ ...prev, [f.key]: e.target.value }))}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        <div style={{ padding:"14px 18px", background:"#fff0f0", borderRadius:10,
                          border:"1.5px solid #fecaca" }}>
                          <div style={{ fontWeight:800, fontSize:"0.88rem", color:"#dc2626", marginBottom:8 }}>
                            ⚠️ Confirmation requise
                          </div>
                          <p style={{ fontSize:"0.82rem", color:"#374151" }}>
                            Modification de <strong>{editSocAdmin.nom || editSocAdmin.name}</strong> — action irréversible.
                          </p>
                        </div>
                        <div style={{ background:"#F8FAFC", borderRadius:10, padding:"14px 16px" }}>
                          <div style={{ fontSize:"0.68rem", fontWeight:700, color:"#94A3B8",
                            textTransform:"uppercase", marginBottom:10 }}>
                            Récapitulatif
                          </div>
                          {Object.entries(editFormAdmin).filter(([,v]) => v).map(([k,v]) => (
                            <div key={k} style={{ display:"flex", justifyContent:"space-between",
                              padding:"6px 0", borderBottom:"1px solid #E2E8F0", fontSize:"0.82rem" }}>
                              <span style={{ color:"#64748B", fontWeight:600, textTransform:"capitalize" }}>{k}</span>
                              <span style={{ color:"#0F172A", fontWeight:700 }}>{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                      <div style={S.modalFoot}>
                        <button style={S.btnCancel} onClick={() => {
                          if (editStepAdmin === 2) setEditStepAdmin(1);
                          else { setEditSocAdmin(null); setEditStepAdmin(1); }
                        }}>
                          {editStepAdmin === 2 ? "← Revenir" : "Annuler"}
                        </button>
                        {editStepAdmin === 1 ? (
                          <button style={{ ...S.btnPrimary, background:"#f59e0b" }}
                            onClick={() => {
                              if (!editFormAdmin.nom?.trim()) {
                                showToast("Le nom est obligatoire", "error"); return;
                              }
                              setEditStepAdmin(2);
                            }}>
                            📝 Continuer →
                          </button>
                        ) : (
                          <button style={{ ...S.btnPrimary, background:"#16a34a" }}
                            onClick={handleSaveEditSocAdmin}
                            disabled={socLoading}>
                            {socLoading ? "⏳ Sauvegarde..." : "✅ Valider & Confirmer"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="statsgrid-responsive" style={S.statsGrid4}>
                  {[
                    { label: "Total Sociétés", value: companies.length,                                      color: "#e11d48", sub: "enregistrées" },
                    { label: "Actives",         value: companies.filter(c => c.active !== false).length,     color: "#43b89c", sub: "opérationnelles" },
                    { label: "Factures Total",  value: middleLoading ? "..." : factures.length,              color: "#6c63ff", sub: "toutes sociétés" },
                    { label: "Avoirs Total",    value: middleLoading ? "..." : avoirs.length,                color: "#f59e0b", sub: "notes de crédit" },
                  ].map((s, i) => <StatCard key={i} {...s} />)}
                </div>

                {middleLoading ? <StateBox loading /> : companies.length === 0 ? (
                  <div style={S.emptyState}>
                    <div style={{ fontSize: "2.5rem" }}>🏢</div>
                    <p style={{ fontSize: "0.84rem", color: "#94A3B8" }}>Aucune société enregistrée</p>
                    <button style={S.btnPrimary} onClick={openCreateSoc}>Créer la première société</button>
                  </div>
                ) : (
                  <div style={S.tableCard}>
                    <table style={S.table}>
                      <thead>
                        <tr>{["#","Société","MF / ID","Adresse","Contact","Statut","Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {companies.map((c, i) => (
                          <tr key={c.id || i} style={S.tr}
                            onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}>
                            <td style={S.td}><span style={S.idNum}>{c.id || i + 1}</span></td>
                            <td style={S.td}>
                              <div style={S.userCell}>
                                <div style={{ ...S.avatar, background: "#e11d48", fontSize: "0.75rem" }}>
                                  {(c.name || c.nom || "?").charAt(0).toUpperCase()}
                                </div>
                                <span style={S.userName3}>{c.name || c.nom || "—"}</span>
                              </div>
                            </td>
                            <td style={S.td}><span style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#64748B" }}>{c.tax_id || c.matricule_fiscale || c.paradox_folder || "—"}</span></td>
                            <td style={S.td}><span style={S.emailCell}>{c.address || c.adresse || "—"}</span></td>
                            <td style={S.td}><span style={S.emailCell}>{c.email || c.phone || c.telephone || "—"}</span></td>
                            <td style={S.td}>
                              <span style={{ fontSize: "0.73rem", fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: c.active !== false ? "#dcfce7" : "#fef3c7", color: c.active !== false ? "#16a34a" : "#d97706" }}>
                                {c.active !== false ? "✓ Active" : "⏳ En attente"}
                              </span>
                            </td>
                            <td style={S.td}>
                              <div style={S.actionsRow}>
                                <button style={{ ...S.actionEdit, color: "#f59e0b", borderColor: "#f59e0b" }} className="btn-hover"
                                  onClick={() => openEditSoc(c)}>✏️ Modifier</button>
                                <button style={{ ...S.actionEdit, color: "#0EA5E9", borderColor: "#0EA5E9" }} className="btn-hover"
                                  onClick={() => { fetchSocStats(c); setActiveTab("stats"); }}>📊 Stats</button>
                                <button style={S.actionDelete} className="btn-hover" onClick={() => setConfirmDelSoc(c)}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

              {/* ══ TAB: TESTS ══ */}
                {activeTab === "tests" && (
                  <div style={S.tabContent}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={S.sectionTitle}>✅ Rapport de Tests — Middleware TEIF</div>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <span style={{ background:"#dcfce7", color:"#16a34a", padding:"4px 12px", borderRadius:20, fontSize:"0.75rem", fontWeight:700 }}>
                          ✅ 16/16 Passed · 0 Failed
                        </span>
                        <span style={{ background:"#f0f9ff", color:"#0891b2", padding:"4px 12px", borderRadius:20, fontSize:"0.75rem", fontWeight:600 }}>
                          pytest-html v4.2.0
                        </span>
                        <button style={S.btnSecondary}
                          onClick={() => window.open(`${MIDDLEWARE_URL}/tests/rapport?token=${encodeURIComponent(getCookie("token") || "")}`, "_blank")}>
                          🔗 Ouvrir dans nouvel onglet
                        </button>
                      </div>
                    </div>

                    {/* Stats rapides */}
                    <div className="statsgrid-responsive" style={S.statsGrid4}>
                      {[
                        { label: "Tests Passed",  value: "16",  color: "#16a34a", sub: "100% réussis"     },
                        { label: "Tests Failed",  value: "0",   color: "#43b89c", sub: "aucun échec"       },
                        { label: "Tests Skipped", value: "0",   color: "#64748B", sub: "aucun ignoré"      },
                        { label: "Durée totale",  value: "15s", color: "#7C3AED", sub: "26-Feb-2026"       },
                      ].map((s, i) => <StatCard key={i} {...s} />)}
                    </div>

                    {/* Iframe rapport */}
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", overflow:"hidden" }}>
                      <div style={{ padding:"12px 20px", borderBottom:"1px solid #F1F5F9", background:"#F8FAFC", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:"1rem" }}>📄</span>
                          <span style={{ fontSize:"0.78rem", fontWeight:700, color:"#334155" }}>rapport_tests.html</span>
                          <span style={{ fontSize:"0.68rem", color:"#94A3B8" }}>C:\middleware\tests\rapport_tests.html</span>
                        </div>
                        <span style={{ fontSize:"0.68rem", color:"#94A3B8" }}>Généré le: 26-Feb-2026 à 11:53:21</span>
                      </div>
                    <div style={{ padding:"32px", textAlign:"center", background:"#F8FAFC" }}>
                    <div style={{ fontSize:"2rem", marginBottom:12 }}>📄</div>
                    <p style={{ color:"#64748B", fontSize:"0.88rem", marginBottom:16 }}>
                      Le rapport pytest est disponible — cliquez pour l'ouvrir
                    </p>
                    <button
                      style={{ background:"#6c63ff", color:"#fff", border:"none", padding:"12px 24px", borderRadius:10, fontWeight:700, cursor:"pointer", fontSize:"0.88rem" }}
                      onClick={() => window.open(`${MIDDLEWARE_URL}/tests/rapport?token=${encodeURIComponent(getCookie("token") || "")}`, "_blank")}>
                      Ouvrir le rapport complet
                    </button>
                  </div>
                </div>
                </div>
                )}

            {/* ══ TAB: STATS PAR SOCIÉTÉ ══ */}
            {activeTab === "stats" && (
              <div style={S.tabContent}>
                <div style={S.sectionTitle}>📊 Statistiques par Société</div>

                {/* Sélectionner société */}
                <div style={S.dssCard}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Sélectionner une société
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {companies
                      .filter((c, i, arr) => arr.findIndex(x => (x.name || x.nom || "").trim().toUpperCase() === (c.name || c.nom || "").trim().toUpperCase()) === i)
                      .map((c, i) => (
                      <button key={i}
                        style={{ padding: "8px 16px", borderRadius: 9, border: `2px solid ${selectedSocStat?.id === c.id ? "#7C3AED" : "#E2E8F0"}`, background: selectedSocStat?.id === c.id ? "#F5F3FF" : "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: selectedSocStat?.id === c.id ? "#7C3AED" : "#334155", fontFamily: "inherit", transition: "all 0.15s" }}
                        onClick={() => fetchSocStats(c)}>
                        {c.name || c.nom || `Société ${i + 1}`}
                      </button>
                    ))}
                  </div>
                </div>

                {!selectedSocStat && (
                  <div style={S.emptyState}>
                    <div style={{ fontSize: "2rem" }}>📊</div>
                    <p style={{ fontSize: "0.84rem", color: "#94A3B8" }}>Sélectionnez une société pour voir ses statistiques</p>
                  </div>
                )}

                {selectedSocStat && socStatsLoading && <StateBox loading />}

                {selectedSocStat && !socStatsLoading && socStats && (
                  <>
                    <div style={{ ...S.dssCard, background: "#F5F3FF", border: "1.5px solid #DDD6FE" }}>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#7C3AED" }}>
                        🏢 {selectedSocStat.name || selectedSocStat.nom}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#94A3B8", marginTop: 4 }}>
                        MF: {selectedSocStat.tax_id || selectedSocStat.matricule_fiscale || "—"} · {selectedSocStat.address || "—"}
                      </div>
                    </div>

                    <div className="statsgrid-responsive" style={S.statsGrid4}>
                      {[
                        { label: "Factures",        value: socStats.factures.length,                                              color: "#6c63ff", sub: "dans le système"  },
                        { label: "Archivées ANCE",  value: socStats.archive?.total ?? "—",                                       color: "#43b89c", sub: "avec UUID ANCE"   },
                        { label: "Acceptées",        value: socStats.archive?.by_status?.ACCEPTED ?? "—",                        color: "#43b89c", sub: "validées"          },
                        { label: "Avoirs",           value: socStats.avoirs.length,                                               color: "#f59e0b", sub: "notes de crédit"  },
                        {
                          label: "Montant Total",
                          value: (() => {
                            const total = socStats.factures.reduce((acc, f) => acc + (Number(f.MontantTotalTTC) || 0), 0);
                            const parts = total.toFixed(3).split(".");
                            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                            const devise = socStats.factures[0]?.DeviseAbrege || "TND";
                            return parts.join(".") + " " + devise;
                          })(),
                          color: "#16a34a",
                          sub: "chiffre d'affaires"
                        },
                      ].map((s, i) => <StatCard key={i} {...s} />)}
                    </div>

                    {socStats.factures.length > 0 && (
                      <>
                        <div style={S.sectionTitle}>🧾 Dernières factures</div>
                        <div style={S.tableCard}>
                          <table style={S.table}>
                            <thead>
                              <tr>{["#","Numéro","Client","Montant","Statut","Date","PDF"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {socStats.factures.slice(0, 10).map((f, i) => (
                                <tr key={i} style={S.tr}
                                  onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                                  <td style={S.td}><span style={S.idNum}>{i + 1}</span></td>
                                  <td style={S.td}><span style={{ fontWeight: 700, color: "#7C3AED" }}>{f.NumfactAffich || f.NumFacture || "—"}</span></td>
                                  <td style={S.td}><span style={S.emailCell}>{f.CodeClient || "—"}</span></td>
                                  <td style={S.td}><span style={{ fontWeight: 700 }}>{f.MontantTotalTTC != null ? `${Number(f.MontantTotalTTC).toFixed(3)} ${f.DeviseAbrege || "TND"}` : "—"}</span></td>
                                  <td style={S.td}><StatusBadge status={f.StatutCommande} /></td>
                                  <td style={S.td}><span style={S.dateCell}>{f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}</span></td>
                                </tr>
                              ))}
                            </tbody>
                            
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

              {activeTab === "stats" && selectedSocStat && !socStatsLoading && socStats && socStats.factures.length > 0 && (
        <div style={S.tabContent}>
          <div style={S.sectionTitle}>💰 Chiffre d'Affaires — {selectedSocStat.name || selectedSocStat.nom}</div>
          {(() => {
            const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
            const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));
            const years = Array.from(new Set(socStats.factures
              .map(f => f.DateFacture ? new Date(f.DateFacture).getFullYear() : null)
              .filter(Boolean))).sort((a,b) => b - a);
            const currentYear = years[0] || new Date().getFullYear();
            const monthlyTotals = MONTHS.map((_, i) =>
              socStats.factures
                .filter(f => {
                  if (!f.DateFacture) return false;
                  const d = new Date(f.DateFacture);
                  return d.getFullYear() === currentYear && d.getMonth() === i;
                })
                .reduce((acc, f) => acc + (Number(f.MontantTotalTTC) || 0), 0)
            );
            const maxVal = Math.max(...monthlyTotals, 1);
            const clientTotals = {};
            socStats.factures.forEach(f => {
              const key = f.CodeClient || "—";
              clientTotals[key] = (clientTotals[key] || 0) + (Number(f.MontantTotalTTC) || 0);
            });
            const clientRows = Object.entries(clientTotals)
              .map(([client, total]) => ({ client, total }))
              .sort((a, b) => b.total - a.total)
              .slice(0, 10);
            const clientTotalSum = Object.values(clientTotals).reduce((a, v) => a + v, 0) || 1;
            const devise = socStats.factures[0]?.DeviseAbrege || "TND";

            return (
              <>
                <div style={{ background: "#fff", borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.05)", border: "1px solid #E2E8F0" }}>
                  <p style={{ margin: "0 0 16px", fontWeight: 700, color: "#334155", fontSize: "0.9rem" }}>CA mensuel — {currentYear}</p>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 200 }}>
                    {monthlyTotals.map((val, i) => (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <div
                          title={fmt(val) + " " + devise}
                          style={{
                            width: "100%",
                            height: Math.max((val / maxVal) * 160, val > 0 ? 4 : 0) + "px",
                            background: val > 0 ? "#7C3AED" : "#eee",
                            borderRadius: "4px 4px 0 0",
                            transition: "height 0.3s",
                          }}
                        />
                        <span style={{ fontSize: 11, color: "#94A3B8" }}>{MONTHS[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.05)", border: "1px solid #E2E8F0" }}>
                  <p style={{ margin: "0 0 16px", fontWeight: 700, color: "#334155", fontSize: "0.9rem" }}>Répartition par client (top 10)</p>
                  {clientRows.length === 0 ? (
                    <p style={{ color: "#94A3B8", fontSize: 13 }}>Aucune donnée disponible.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {clientRows.map((r, i) => {
                        const pct = Math.round((r.total / clientTotalSum) * 100);
                        return (
                          <div key={i}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                              <span>{r.client}</span>
                              <span style={{ color: "#94A3B8" }}>{fmt(r.total)} {devise} · {pct}%</span>
                            </div>
                            <div style={{ height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: pct + "%", background: "#7C3AED", borderRadius: 4 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}


        {activeTab === "factures" && (
          <div style={S.tabContent}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
              <div style={S.sectionTitle}>🧾 Toutes les factures — Vue Super Admin</div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <div style={S.dssCard}>
                  <div style={S.dssRow}>
                    <input style={S.input} type="number" placeholder="N° facture..." value={dssNumFacture} onChange={e => setDssNumFacture(e.target.value)} />
                    <button style={S.btnPrimary} onClick={handleDssSubmit} disabled={dssLoading}>
                      {dssLoading ? "⏳..." : "📤 Envoyer ANCE"}
                    </button>
                  </div>
                  {dssResult && (
                    <div style={{ ...S.resultBox, background: dssResult.success ? "#dcfce7" : "#fee2e2", color: dssResult.success ? "#166534" : "#991b1b" }}>
                      {dssResult.success ? <>✅ UUID: {dssResult.uuid_fiscal}</> : <>❌ {dssResult.detail}</>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Filtres société ── */}
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ fontSize:"0.78rem", fontWeight:700, color:"#64748B" }}>Société:</span>
              <button
                style={{ padding:"7px 14px", borderRadius:8, border:`2px solid ${!selectedSocStat ? "#7C3AED" : "#E2E8F0"}`, background:!selectedSocStat?"#F5F3FF":"#fff", cursor:"pointer", fontSize:"0.78rem", fontWeight:600, color:!selectedSocStat?"#7C3AED":"#64748B", fontFamily:"inherit" }}
                onClick={() => { setSelectedSocStat(null); setFactures([]); fetchAllMiddleware(); }}>
                Toutes
              </button>
              {companies.map((c, i) => (
                <button key={i}
                  style={{ padding:"7px 14px", borderRadius:8, border:`2px solid ${selectedSocStat?.id === c.id ? "#7C3AED" : "#E2E8F0"}`, background:selectedSocStat?.id === c.id?"#F5F3FF":"#fff", cursor:"pointer", fontSize:"0.78rem", fontWeight:600, color:selectedSocStat?.id === c.id?"#7C3AED":"#64748B", fontFamily:"inherit", transition:"all 0.15s" }}
                  onClick={async () => {
                    setSelectedSocStat(c);
                    setMiddleLoading(true);
                    setFactures([]);
              try {
                const societeName = (c.name || c.nom || "").trim();
                const BRIDGE_URL = process.env.REACT_APP_BRIDGE_URL;
                const res = await fetch(
                  `${BRIDGE_URL}/factures?societe_nom=${encodeURIComponent(societeName)}&exercice=${parseInt(localStorage.getItem("bridge_exercice")) || new Date().getFullYear()}`,
                  { headers: authH() }
                );
                const data = await res.json();
                const list = Array.isArray(data) ? data : (data.factures || data.data || []);
                setFactures(list);
                showToast(list.length > 0 ? `✅ ${list.length} factures — ${societeName}` : `Aucune facture`, list.length > 0 ? "success" : "error");
              } catch (err) {
                showToast("Erreur chargement factures", "error");
                setFactures([]);
              } finally {
                setMiddleLoading(false);
              }
            }}>
            {c.name || c.nom || `Société ${i+1}`}
          </button>
        ))}
      </div>

      {/* ── Barre info + search ── */}
      {selectedSocStat && factures.length > 0 && (() => {
        const totalTTC = factures.reduce((acc,f) => acc + (f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : 0), 0);
        const statuts = factures.reduce((acc,f) => {
          const s = (f.StatutCommande || f.Statut || f.status || "C").toUpperCase();
          acc[s] = (acc[s]||0)+1; return acc;
        }, {});
        return (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
            {[
              { label:"Total factures", value: factures.length,              color:"#7C3AED", sub:"chargées" },
              { label:"Total TTC", value: totalTTC.toFixed(3)+" "+(factures[0]?.DeviseAbrege || "TND"), color:"#16a34a", sub:"montant global" },
              { label:"Créées",         value: statuts["C"]||0,              color:"#2563eb", sub:"en attente" },
              { label:"Validées",       value: statuts["V"]||0,              color:"#16a34a", sub:"ok ERP" },
            ].map((s,i) => <StatCard key={i} {...s} />)}
          </div>
        );
      })()}

      {/* ── Empty states ── */}
      {middleLoading ? <StateBox loading /> : !selectedSocStat ? (
        <div style={S.emptyState}>
          <div style={{ fontSize:"2.5rem" }}>🏢</div>
          <p style={{ fontSize:"0.84rem", color:"#94A3B8", fontWeight:500 }}>Sélectionnez une société pour voir ses factures</p>
        </div>
      ) : factures.length === 0 ? <EmptyState icon="🧾" text="Aucune facture pour cette société" /> : (() => {

        const STATUT_SA = {
          C:         { label:"Créée",        color:"#2563eb", bg:"#dbeafe",  icon:"📝", step:0 },
          V:         { label:"Validée",      color:"#16a34a", bg:"#dcfce7",  icon:"✅", step:1 },
          SIGNED:    { label:"Signée",       color:"#7c3aed", bg:"#ede9fe",  icon:"🔏", step:2 },
          SUBMITTED: { label:"Soumise",      color:"#0891b2", bg:"#cffafe",  icon:"📤", step:3 },
          ACCEPTED:  { label:"Acceptée",     color:"#16a34a", bg:"#dcfce7",  icon:"🏛️", step:4 },
          REJECTED:  { label:"Rejetée",      color:"#dc2626", bg:"#fee2e2",  icon:"❌", step:4 },
          ARCHIVED:  { label:"Archivée",     color:"#e11d48", bg:"#ffe4e6",  icon:"🗄️", step:5 },
        };

        const STEPS = [
          { icon:"🏭", label:"ERP",       color:"#6c63ff" },
          { icon:"📄", label:"XML TEIF",  color:"#0EA5E9" },
          { icon:"✅", label:"Validée",   color:"#43b89c" },
          { icon:"🔏", label:"Signée",    color:"#7c3aed" },
          { icon:"🏛️", label:"ANCE",     color:"#f59e0b" },
          { icon:"🗄️", label:"Archivée", color:"#e11d48" },
        ];

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {factures.map((f, i) => {
              const statutKey = (f.StatutCommande || f.Statut || f.status || "C").toUpperCase();
              const st = STATUT_SA[statutKey] || { label:statutKey, color:"#64748b", bg:"#f1f5f9", icon:"❓", step:0 };
              const stepIdx = st.step ?? 0;
              const ttc = f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : null;
              const ht  = f.MontantTotalHT  != null ? Number(f.MontantTotalHT)  : null;
              const tva = f.MontantTVA1     != null ? Number(f.MontantTVA1)     : null;
              const devise = f.DeviseAbrege || "TND";
              const numDisplay = f.NumfactAffich || f.NumFacture || f.num;
              const dateStr = f.DateFacture || f.date;

              return (
                <div key={i} style={{
                  background:"#fff",
                  borderRadius:16,
                  border:"1.5px solid #e8e8f0",
                  overflow:"hidden",
                  transition:"all 0.2s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor="#7C3AED"; e.currentTarget.style.boxShadow="0 8px 32px rgba(124,58,237,0.10)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor="#e8e8f0"; e.currentTarget.style.boxShadow="none"; }}>

                  {/* ── Bande colorée top ── */}
                  <div style={{ height:3, background:`linear-gradient(90deg,${st.color},${STEPS[stepIdx]?.color||st.color})` }} />

                  <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>

                    {/* ── Header row ── */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        {/* Avatar statut */}
                        <div style={{ width:50, height:50, borderRadius:14, background:st.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem", flexShrink:0, border:`2px solid ${st.color}25` }}>
                          {st.icon}
                        </div>
                        <div>
                          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                            <span style={{ fontWeight:800, color:"#7C3AED", fontSize:"1.1rem", fontFamily:"monospace" }}>
                              #{numDisplay || "—"}
                            </span>
                            {f.NumFactureComptable && (
                              <span style={{ fontSize:"0.68rem", color:"#94A3B8", background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:6, padding:"2px 8px", fontFamily:"monospace" }}>
                                Compt. {f.NumFactureComptable}
                              </span>
                            )}
                          </div>
                          <div style={{ display:"flex", gap:10, marginTop:5, flexWrap:"wrap", alignItems:"center" }}>
                            <span style={{ fontSize:"0.75rem", color:"#64748B" }}>
                              👤 <strong style={{ color:"#334155" }}>{f.NomClient || f.CodeClient || "—"}</strong>
                            </span>
                            {dateStr && (
                              <span style={{ fontSize:"0.75rem", color:"#94A3B8" }}>
                                📅 {new Date(dateStr).toLocaleDateString("fr-FR")}
                              </span>
                            )}
                            {f.Site && (
                              <span style={{ fontSize:"0.68rem", background:"#F0F9FF", color:"#0891b2", borderRadius:20, padding:"2px 8px", fontWeight:700 }}>
                                {f.Site}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Badges droite */}
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"6px 14px", borderRadius:20, fontSize:"0.76rem", fontWeight:700, color:st.color, background:st.bg, border:`1.5px solid ${st.color}30` }}>
                          {st.icon} {st.label}
                        </span>
                        <span style={{ fontSize:"0.68rem", color:"#94A3B8", background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:20, padding:"4px 10px", fontWeight:600 }}>
                          Étape {stepIdx+1}/6
                        </span>
                      </div>
                    </div>

                    {/* ── Montants ── */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1.5fr auto", gap:1, background:"#F1F5F9", borderRadius:12, overflow:"hidden" }}>
                      {[
                        { label:"Montant HT",  val:ht,  color:"#0891b2" },
                        { label:"TVA",         val:tva, color:"#f59e0b" },
                        { label:"Total TTC",   val:ttc, color:"#7C3AED", big:true },
                      ].map((m,mi) => (
                        <div key={mi} style={{ background:"#fff", padding:"14px 18px", display:"flex", flexDirection:"column", gap:3 }}>
                          <span style={{ fontSize:"0.6rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.07em" }}>{m.label}</span>
                          <span style={{ fontSize: m.big ? "1.05rem" : "0.88rem", fontWeight: m.big ? 800 : 700, color:m.color }}>
                            {m.val != null ? m.val.toFixed(3)+" "+devise : "—"}
                          </span>
                        </div>
                      ))}
                      {/* Actions rapides intégrées */}
                      <div style={{ background:"#fff", padding:"10px 14px", display:"flex", flexDirection:"column", gap:6, justifyContent:"center" }}>
                        <button style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #ddd6fe", background:"#f5f3ff", cursor:"pointer", fontSize:"0.7rem", fontWeight:700, color:"#7c3aed", fontFamily:"inherit", whiteSpace:"nowrap" }}
                          onClick={async () => {
                            const numFact = f.NumFacture || f.NumfactAffich || f.num;
                            console.log("🔍 PDF click — numFact:", numFact, "| fields:", { NumFacture: f.NumFacture, NumfactAffich: f.NumfactAffich, num: f.num });
                            
                            if (!numFact) { showToast("❌ Numéro facture introuvable", "error"); return; }
                            
                            try {
                              const url = `${MIDDLEWARE_URL}/factures/${numFact}/pdf?societe_id=${selectedSocStat?.id || ""}`;
                              console.log("📄 PDF URL:", url);
                              
                              const res = await fetch(url, { headers: authH() });
                              if (!res.ok) { showToast(`PDF non disponible (${res.status})`, "error"); return; }
                              const blob = await res.blob();
                              window.open(URL.createObjectURL(blob), "_blank");
                            } catch (err) { 
                              console.error("PDF error:", err);
                              showToast("Erreur PDF", "error"); 
                            }
                          }}>📄 PDF</button>
                        {statutKey !== "ACCEPTED" && statutKey !== "ARCHIVED" && (
                          <button style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #bae6fd", background:"#f0f9ff", cursor:"pointer", fontSize:"0.7rem", fontWeight:700, color:"#0891b2", fontFamily:"inherit", whiteSpace:"nowrap" }}
                            onClick={async () => {
                              const res = await fetch(`${MIDDLEWARE_URL}/factures/${f.NumFacture||f.num}/submit-dss`, { method:"POST", headers:authH() });
                              const data = await res.json();
                              if (data.success) {
                                showToast(`✅ Facture ${numDisplay} → ANCE`);
                                handlePublishEvent(f.NumFacture || f.num, "INVOICE_CREATED");
                              } else showToast(data.detail || "Erreur ANCE", "error");
                            }}>📤 ANCE</button>
                        )}
                        {statutKey === "REJECTED" && (
                          <button style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #fed7aa", background:"#fff7ed", cursor:"pointer", fontSize:"0.7rem", fontWeight:700, color:"#ea580c", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                            🔄 Retry
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ── Progress TEIF ── */}
                    <div style={{ display:"flex", alignItems:"center", gap:0 }}>
                      {STEPS.map((step, si) => {
                        const done = si <= stepIdx;
                        const current = si === stepIdx;
                        return (
                          <React.Fragment key={si}>
                            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                              <div style={{
                                width: current ? 32 : 26,
                                height: current ? 32 : 26,
                                borderRadius:"50%",
                                background: done ? step.color : "#E2E8F0",
                                display:"flex", alignItems:"center", justifyContent:"center",
                                fontSize: current ? "0.8rem" : "0.65rem",
                                boxShadow: current ? `0 0 0 3px ${step.color}30` : "none",
                                transition:"all 0.2s",
                                flexShrink:0,
                              }}>{step.icon}</div>
                              <span style={{ fontSize:"0.55rem", fontWeight: current?700:400, color: done?step.color:"#CBD5E1", whiteSpace:"nowrap" }}>
                                {step.label}
                              </span>
                            </div>
                            {si < STEPS.length-1 && (
                              <div style={{ flex:1, height:2, background: si < stepIdx ? STEPS[si+1].color : "#E2E8F0", margin:"0 2px 16px", transition:"all 0.3s" }} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  )}
        {/* ══ TAB: CLIENTS ══ */}
        {activeTab === "clients" && (
          <div style={S.tabContent}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div style={S.sectionTitle}>👥 Clients depuis ERP Paradox</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <div style={S.searchBox}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input style={S.searchInput} placeholder="Rechercher par nom, code, ville, MF..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button style={S.searchClear} onClick={() => setSearch("")}>×</button>}
          </div>
          {["Tous","Résident","Non-résident"].map(f => (
            <button key={f}
              style={{ padding:"7px 12px", borderRadius:8, border:`1.5px solid ${(window._clientFilter||"Tous")===f ? "#0EA5E9" : "#E2E8F0"}`, background:(window._clientFilter||"Tous")===f ? "#F0F9FF" : "#fff", cursor:"pointer", fontSize:"0.75rem", fontWeight:600, color:(window._clientFilter||"Tous")===f ? "#0EA5E9" : "#64748B", fontFamily:"inherit" }}
              onClick={() => { window._clientFilter = f; setSearch(search+" "); setTimeout(()=>setSearch(s=>s.trim()),10); }}>
              {f}
            </button>
          ))}
        </div>
            </div>

            {/* Filtre par société */}
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ fontSize:"0.78rem", fontWeight:700, color:"#64748B" }}>Filtrer par société:</span>
              {companies.map((c, i) => (
                <button key={i}
                  style={{ padding:"7px 14px", borderRadius:8,
                    border:`2px solid ${selectedSocStat?.id === c.id ? "#0EA5E9" : "#E2E8F0"}`,
                    background: selectedSocStat?.id === c.id ? "#F0F9FF" : "#fff",
                    cursor:"pointer", fontSize:"0.78rem", fontWeight:600,
                    color: selectedSocStat?.id === c.id ? "#0EA5E9" : "#64748B",
                    fontFamily:"inherit", transition:"all 0.15s" }}
                  onClick={async () => {
                    setSelectedSocStat(c);
                    setMiddleLoading(true);
                    setClients([]);
                    try {
                      const societeNom = (c.name || c.nom || "").trim();
                    const BRIDGE_URL = process.env.REACT_APP_BRIDGE_URL;
                    const res = await fetch(
                      `${BRIDGE_URL}/clients?societe_nom=${encodeURIComponent(societeNom)}&exercice=${parseInt(localStorage.getItem("bridge_exercice")) || new Date().getFullYear()}`,
                      { headers: authH() }
                    );
                      
                      const data = await res.json();
                      const list = Array.isArray(data) ? data : (data.clients || data.data || []);
                      setClients(list);
                      showToast(list.length > 0 ? `✅ ${list.length} clients chargés` : `Aucun client`, list.length > 0 ? "success" : "error");
                    } catch (err) {
                      showToast("Erreur chargement clients", "error");
                      setClients([]);
                    } finally {
                      setMiddleLoading(false);
                    }
                  }}>
                  {c.name || c.nom || `Société ${i+1}`}
                </button>
              ))}
            </div>

            {!selectedSocStat ? (
              <div style={S.emptyState}>
                <div style={{ fontSize:"2rem" }}>🏢</div>
                <p style={{ fontSize:"0.84rem", color:"#94A3B8" }}>Sélectionnez une société pour voir ses clients</p>
              </div>
            ) : middleLoading ? <StateBox loading /> :
              clients.length === 0 ? <EmptyState icon="👥" text="Aucun client" /> : (() => {
              const filtered = clients.filter(c => !search ||
                String(c.Code||c.CodeClient||"").toLowerCase().includes(search.toLowerCase()) ||
                String(c.Nom||c.nom||c.name||"").toLowerCase().includes(search.toLowerCase()) ||
                String(c.Ville||"").toLowerCase().includes(search.toLowerCase())
              );
              return (
                <>
                  <div style={{ fontSize:"0.78rem", color:"#64748B", fontWeight:600 }}>
                    <strong style={{ color:"#0F172A" }}>{filtered.length}</strong> client(s)
                    {clients.length !== filtered.length && <span style={{ color:"#94A3B8" }}> sur {clients.length}</span>}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px,1fr))", gap:14 }}>
                    
                    {filtered.map((c, i) => {
                      const code   = c.Code || c.CodeClient || "—";
                      const nom    = c.Nom  || c.nom || c.name || "—";
                      const abrege = c.NomAbrege || "";
                      const type   = c.TypeClient || "";
                      const ville  = c.Ville || "";
                      const pays   = c.Pays || "";
                      const tel    = c.Tel || c.Telephone || "";
                      const email  = c.Email || "";
                      const eori   = c.EORI || "";
                      const mf     = c.CodeFiscale || c.MatriculeFiscal || "";
                      // Helper: calcul tier haseb données disponibles
                      const getTier = (c) => {
                        const score = getScore(c);
                        const tier = score >= 80
                          ? { label:"Gold",    icon:"★", color:"#d97706", bg:"#fef3c7", gradient:"linear-gradient(90deg,#f59e0b,#d97706)" }
                          : score >= 50
                          ? { label:"Silver",  icon:"◆", color:"#0891b2", bg:"#e0f2fe", gradient:"linear-gradient(90deg,#38bdf8,#0891b2)" }
                          : { label:"Standard",icon:"○", color:"#64748B", bg:"#F8FAFC", gradient:"linear-gradient(90deg,#94A3B8,#64748B)" };

                        const LETTER_COLORS = {
                          A:{bg:"#fce7f3",color:"#be185d"}, B:{bg:"#ede9fe",color:"#7c3aed"},
                          C:{bg:"#dbeafe",color:"#1d4ed8"}, D:{bg:"#dcfce7",color:"#15803d"},
                          E:{bg:"#fef3c7",color:"#d97706"}, F:{bg:"#fee2e2",color:"#dc2626"},
                          G:{bg:"#f0fdf4",color:"#16a34a"}, H:{bg:"#f0f9ff",color:"#0891b2"},
                          I:{bg:"#fdf4ff",color:"#9333ea"}, J:{bg:"#fff7ed",color:"#ea580c"},
                          K:{bg:"#ecfdf5",color:"#059669"}, L:{bg:"#eff6ff",color:"#2563eb"},
                          M:{bg:"#fdf2f8",color:"#c026d3"}, N:{bg:"#f0fdfa",color:"#0d9488"},
                          O:{bg:"#fff1f2",color:"#e11d48"}, P:{bg:"#f5f3ff",color:"#7c3aed"},
                          Q:{bg:"#fefce8",color:"#ca8a04"}, R:{bg:"#fef2f2",color:"#ef4444"},
                          S:{bg:"#f0f9ff",color:"#0284c7"}, T:{bg:"#f7fee7",color:"#65a30d"},
                          U:{bg:"#ede9fe",color:"#6d28d9"}, V:{bg:"#fdf4ff",color:"#a21caf"},
                          W:{bg:"#fff7ed",color:"#c2410c"}, X:{bg:"#ecfeff",color:"#0e7490"},
                          Y:{bg:"#fefce8",color:"#a16207"}, Z:{bg:"#fdf2f8",color:"#9d174d"},
                        };

                        const nom = c.Nom || c.nom || c.name || "?";
                        const firstLetter = nom.charAt(0).toUpperCase();
                        const av = LETTER_COLORS[firstLetter] || { bg:"#F1F5F9", color:"#64748B" };

                        return { ...tier, avatarBg: av.bg, avatarColor: av.color };
                      };

                      // Helper: score activité basé sur complétude des données
                      const getScore = (c) => {
                        let score = 0;
                        if (c.CodeFiscale || c.MatriculeFiscal) score += 40;  // MF = plus important
                        if (c.Email)                             score += 20;
                        if (c.Tel || c.Telephone)               score += 15;
                        if (c.Ville)                             score += 15;
                        if (c.EORI)                              score += 10;
                        return score;
                      };

                      return (
                        <div key={i} style={{ 
                          background:"#fff", borderRadius:16, 
                          border:`1.5px solid ${!mf ? "#fde68a" : "#e8e8f0"}`,  // alerte si pas de MF
                          padding:"20px", display:"flex", flexDirection:"column", gap:12, 
                          transition:"all 0.2s", position:"relative", overflow:"hidden"
                        }} className="stat-hover">

                          {/* Bande colorée top haseb tier */}
                          {(() => {
                            const tier = getTier(c);
                            return <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background: tier.gradient }} />;
                          })()}

                          {/* Header */}
                          <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginTop:4 }}>
                            <div style={{ width:48, height:48, borderRadius:13, background: getTier(c).avatarBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", fontWeight:800, color: getTier(c).avatarColor, flexShrink:0, border:`2px solid ${getTier(c).avatarColor}25` }}>
                              {(nom !== "—" ? nom.charAt(0) : "?").toUpperCase()}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:800, color:"#0F172A", fontSize:"0.92rem", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                {nom}
                              </div>
                              {abrege && abrege !== nom && (
                                <div style={{ fontSize:"0.68rem", color:"#94A3B8", marginTop:1 }}>{abrege}</div>
                              )}
                              <div style={{ display:"flex", gap:5, marginTop:5, flexWrap:"wrap", alignItems:"center" }}>
                                <span style={{ background:"#F8FAFC", color:"#64748B", borderRadius:20, padding:"2px 8px", fontSize:"0.65rem", fontWeight:700, border:"1px solid #E2E8F0", fontFamily:"monospace" }}>
                                  {code}
                                </span>
                                {type && (
                                  <span style={{ background: type==="R" ? "#dbeafe" : "#dcfce7", color: type==="R" ? "#1d4ed8" : "#16a34a", borderRadius:20, padding:"2px 8px", fontSize:"0.65rem", fontWeight:700 }}>
                                    {type === "R" ? "Résident" : type === "N" ? "Non-résident" : type}
                                  </span>
                                )}
                                {/* TIER BADGE */}
                                <span style={{ background: getTier(c).bg, color: getTier(c).color, borderRadius:20, padding:"2px 8px", fontSize:"0.65rem", fontWeight:800, border:`1px solid ${getTier(c).color}30` }}>
                                  {getTier(c).icon} {getTier(c).label}
                                </span>
                              </div>
                            </div>
                            {/* Alerte MF manquant */}
                            {!mf && (
                              <div style={{ flexShrink:0, background:"#fef3c7", color:"#d97706", borderRadius:8, padding:"4px 8px", fontSize:"0.65rem", fontWeight:700 }}>
                                ⚠️ MF manquant
                              </div>
                            )}
                          </div>

                          {/* Score activité — barre visuelle */}
                          <div style={{ background:"#F8FAFC", borderRadius:9, padding:"10px 14px" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                              <span style={{ fontSize:"0.62rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.07em" }}>Score activité</span>
                              <span style={{ fontSize:"0.72rem", fontWeight:800, color: getTier(c).color }}>{getScore(c)}%</span>
                            </div>
                            <div style={{ height:5, background:"#E2E8F0", borderRadius:99, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${getScore(c)}%`, background: getTier(c).gradient, borderRadius:99, transition:"width 0.8s ease" }} />
                            </div>
                          </div>

                          {/* Infos grid */}
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
                            {(ville || pays) && (
                              <div style={{ background:"#F8FAFC", borderRadius:8, padding:"8px 10px" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>Localisation</div>
                                <div style={{ fontSize:"0.76rem", fontWeight:600, color:"#0F172A", marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                  {[ville, pays].filter(Boolean).join(", ")}
                                </div>
                              </div>
                            )}
                            {tel && (
                              <div style={{ background:"#F8FAFC", borderRadius:8, padding:"8px 10px" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>Téléphone</div>
                                <div style={{ fontSize:"0.76rem", fontWeight:600, color:"#0F172A", marginTop:2 }}>{tel}</div>
                              </div>
                            )}
                            {mf && (
                              <div style={{ background:"#F8FAFC", borderRadius:8, padding:"8px 10px", gridColumn:"1/-1" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>Matricule Fiscal</div>
                                <div style={{ fontSize:"0.74rem", fontWeight:700, color:"#7C3AED", marginTop:2, fontFamily:"monospace" }}>{mf}</div>
                              </div>
                            )}
                            {email && (
                              <div style={{ background:"#F8FAFC", borderRadius:8, padding:"8px 10px", gridColumn:"1/-1" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>Email</div>
                                <div style={{ fontSize:"0.74rem", fontWeight:600, color:"#0891b2", marginTop:2 }}>{email}</div>
                              </div>
                            )}
                          </div>

                          {/* Action rapide — voir factures */}
                          <button style={{ padding:"8px", borderRadius:8, border:"1px solid #E2E8F0", background:"#F8FAFC", cursor:"pointer", fontSize:"0.72rem", fontWeight:700, color:"#7C3AED", fontFamily:"inherit", transition:"all 0.15s", textAlign:"center" }}
                            onClick={() => {
                              setSelectedSocStat(companies.find(comp => comp.id === selectedSocStat?.id));
                              setActiveTab("factures");
                              // Filter par client automatiquement si possible
                              showToast(`🔍 Voir factures de ${nom}`);
                            }}>
                            Voir les factures →
                          </button>
                        </div>
                      );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}

                                  {/* ══ TAB: ARCHIVE ══ */}
                                  {activeTab === "archive" && (
                                  <div style={S.tabContent}>
                                    <div className="statsgrid-responsive" style={S.statsGrid4}>
                                      {[
                                        { label: "Total archivées", value: middleLoading ? "..." : (archiveStats?.total ?? archive.length), color: "#43b89c", sub: "avec UUID ANCE" },
                                        { label: "Acceptées",       value: middleLoading ? "..." : (archiveStats?.by_status?.ACCEPTED ?? "—"), color: "#43b89c", sub: "validées" },
                                        { label: "Rejetées",        value: middleLoading ? "..." : (archiveStats?.by_status?.REJECTED ?? "—"), color: "#e74c3c", sub: "à corriger" },
                                        { label: "En cours",        value: middleLoading ? "..." : (archiveStats?.pending ?? "—"), color: "#f0a500", sub: "en traitement" },
                                      ].map((s, i) => <StatCard key={i} {...s} />)}
                                    </div>

                                    {/* ── Filtre par société ── */}
                                <div style={{ fontSize:"0.72rem", color:"#94A3B8", fontStyle:"italic" }}>
                                  📊 Vue globale — toutes sociétés confondues
                                </div>
                          {/* ── Cards archive ── */}
                          <div style={S.sectionTitle}>🗄️ Factures archivées ANCE</div>
                          {middleLoading ? <StateBox loading /> : archive.length === 0 ? <EmptyState icon="🗄️" text="Aucune facture archivée" /> : (() => {
                            const filtered = archive;

                            if (filtered.length === 0) return <EmptyState icon="🗄️" text="Aucune facture archivée pour cette société" />;

                            return (
                              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                                {filtered.map((a, i) => {
                                  const st = a.status?.toUpperCase();
                                  const stColor = st === "ACCEPTED" ? "#16a34a" : st === "REJECTED" ? "#dc2626" : "#d97706";
                                  const stBg    = st === "ACCEPTED" ? "#dcfce7" : st === "REJECTED" ? "#fee2e2" : "#fef3c7";
                                  const stIcon  = st === "ACCEPTED" ? "✅" : st === "REJECTED" ? "❌" : "⏳";
                                  const stLabel = st === "ACCEPTED" ? "Acceptée" : st === "REJECTED" ? "Rejetée" : a.status || "—";

                                  return (
                                    <div key={i} style={{ background:"#fff", borderRadius:16, border:"1.5px solid #e8e8f0", overflow:"hidden", transition:"all 0.2s" }}
                                      onMouseEnter={e => { e.currentTarget.style.borderColor="#43b89c"; e.currentTarget.style.boxShadow="0 8px 32px rgba(67,184,156,0.12)"; }}
                                      onMouseLeave={e => { e.currentTarget.style.borderColor="#e8e8f0"; e.currentTarget.style.boxShadow="none"; }}>

                                      {/* Bande colorée top */}
                                      <div style={{ height:3, background:`linear-gradient(90deg,${stColor},#43b89c)` }} />

                                      <div style={{ padding:"18px 22px", display:"flex", flexDirection:"column", gap:12 }}>

                                        {/* Header */}
                                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                                          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                                            <div style={{ width:46, height:46, borderRadius:12, background:stBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0, border:`2px solid ${stColor}25` }}>
                                              {stIcon}
                                            </div>
                                            <div>
                                              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                                <span style={{ fontWeight:800, color:"#43b89c", fontSize:"1.05rem", fontFamily:"monospace" }}>
                                                  #{a.invoice_number || a.num || "—"}
                                                </span>
                                                {a.societe_nom && (
                                                  <span style={{ fontSize:"0.68rem", color:"#94A3B8", background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:6, padding:"2px 8px" }}>
                                                    {a.societe_nom}
                                                  </span>
                                                )}
                                              </div>
                                              <div style={{ fontSize:"0.73rem", color:"#94A3B8", marginTop:3 }}>
                                                📅 {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}
                                                {a.legal_retention_until && (
                                                  <span style={{ marginLeft:10 }}>🔒 Rétention: {new Date(a.legal_retention_until).toLocaleDateString("fr-FR")}</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"6px 14px", borderRadius:20, fontSize:"0.76rem", fontWeight:700, color:stColor, background:stBg, border:`1.5px solid ${stColor}30` }}>
                                            {stIcon} {stLabel}
                                          </span>
                                        </div>
                                        {/* UUID ANCE */}
                                        <div style={{ background:"#F8FAFC", borderRadius:10, padding:"12px 16px" }}>
                                          <div style={{ fontSize:"0.6rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>UUID ANCE</div>
                                          <div style={{ fontFamily:"monospace", fontSize:"0.8rem", fontWeight:700, color:"#43b89c", wordBreak:"break-all" }}>
                                            {a.uuid_dss || a.uuid || "—"}
                                          </div>
                                        </div>
                                        {/* Intégrité + Actions */}
                                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
                                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                            <span style={{ fontSize:"0.72rem", fontWeight:700, color: a.integrity ? "#16a34a" : "#94A3B8", background: a.integrity ? "#dcfce7" : "#F8FAFC", borderRadius:20, padding:"3px 10px", border:`1px solid ${a.integrity ? "#bbf7d0" : "#E2E8F0"}` }}>
                                              {a.integrity ? "✓ Intégrité OK" : "— Intégrité non vérifiée"}
                                            </span>
                                            {a.environment && (
                                              <span style={{ fontSize:"0.68rem", fontWeight:700, color: a.environment==="simulation" ? "#0891b2" : "#16a34a", background: a.environment==="simulation" ? "#e0f2fe" : "#dcfce7", borderRadius:20, padding:"3px 10px" }}>
                                                {a.environment === "simulation" ? "🧪 Simulation" : "🏭 Production"}
                                              </span>
                                            )}
                                          </div>
                                          {/* ── Bouton Vérifier intégrité ── */}
                                          {!a.integrity && (
                                            <button
                                              style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #c4b5fd", background:"#faf5ff", cursor:"pointer", fontSize:"0.72rem", fontWeight:700, color:"#7c3aed", fontFamily:"inherit", transition:"all 0.15s" }}
                                              onClick={async () => {
                                                try {
                                                  const res = await fetch(
                                                    `${MIDDLEWARE_URL}/archive/invoices/${a.invoice_number || a.num}/integrity`,
                                                    { headers: authH() }
                                                  );
                                                  const data = await res.json();
                                                  if (data.valid || data.integrity_valid) {
                                                    showToast(`✅ Intégrité confirmée — #${a.invoice_number || a.num}`);
                                                  } else {
                                                    showToast(`⚠️ Hash invalide — #${a.invoice_number || a.num}`, "error");
                                                  }
                                                } catch {
                                                  showToast("Erreur vérification intégrité", "error");
                                                }
                                              }}>
                                              🔐 Vérifier intégrité
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                        {/* ══ TAB: TRACABILITÉ ══ */}
                        {activeTab === "tracabilite" && (
                          <div style={S.tabContent}>

                            {/* ── Stats ── */}
                            <div className="statsgrid-responsive" style={S.statsGrid4}>
                              {[
                                { label:"Soumises",     value: tracabilite?.by_status?.ARCHIVED  ?? "—", color:"#7C3AED", sub:"envoyées ANCE"  },
                                { label:"Acceptées",    value: tracabilite?.by_status?.ACCEPTED  ?? "—", color:"#43b89c", sub:"validées"        },
                                { label:"Rejetées",     value: tracabilite?.by_status?.REJECTED  ?? "—", color:"#e74c3c", sub:"à corriger"      },
                                { label:"Retry engine", value: tracabilite?.by_status?.CREDITED  ?? "—", color:"#f59e0b", sub:"en file retry"   },
                              ].map((s, i) => <StatCard key={i} {...s} />)}
                            </div>

                            {/* ── Filtre par société ── */}
                            <div style={{ fontSize:"0.72rem", color:"#94A3B8", fontStyle:"italic" }}>
                              📊 Vue globale — toutes sociétés confondues
                            </div>
                          {/* ── Workflow global ── */}
                          <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", padding:"22px 28px" }}>
                            <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:18 }}>
                              📡 État du pipeline TEIF — Toutes sociétés
                            </div>
                            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
                              {[
                                { icon:"🏭", label:"ERP",       color:"#6c63ff", value: tracabilite?.total_submitted ?? 0 },
                                { icon:"📄", label:"XML TEIF",  color:"#0EA5E9", value: tracabilite?.total_submitted ?? 0 },
                                { icon:"✅", label:"Validées",  color:"#43b89c", value: tracabilite?.total_accepted  ?? 0 },
                                { icon:"🔏", label:"Signées",   color:"#7c3aed", value: tracabilite?.total_submitted ?? 0 },
                                { icon:"🏛️", label:"ANCE",     color:"#f59e0b", value: tracabilite?.total_accepted  ?? 0 },
                                { icon:"🗄️", label:"Archivées",color:"#e11d48", value: tracabilite?.total_accepted  ?? 0 },
                              ].map((step, si) => (
                                <div key={si} style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:1, position:"relative" }}>
                                  {si < 5 && <div style={{ position:"absolute", top:18, left:"50%", width:"100%", height:2, background:"linear-gradient(90deg,"+step.color+"40,#E2E8F0)", zIndex:0 }} />}
                                  <div style={{ width:36, height:36, borderRadius:"50%", background:step.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.9rem", zIndex:1, boxShadow:`0 4px 12px ${step.color}40`, marginBottom:8 }}>
                                    {step.icon}
                                  </div>
                                  <div style={{ fontSize:"0.62rem", fontWeight:700, color:"#64748B", textAlign:"center" }}>{step.label}</div>
                                  <div style={{ fontSize:"0.88rem", fontWeight:800, color:step.color, marginTop:2 }}>{step.value}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                      {/* ── Alertes ── */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <div style={S.sectionTitle}>🔔 Alertes actives ({alerts.length})</div>
                        {alerts.length > 0 && (
                          <span style={{ fontSize:"0.72rem", fontWeight:700, color:"#e74c3c", background:"#fee2e2", borderRadius:20, padding:"3px 12px" }}>
                            {alerts.length} alerte{alerts.length > 1 ? "s" : ""} en cours
                          </span>
                        )}
                      </div>

                      {alerts.length === 0 ? (
                        <div style={{ background:"#f0fdf4", borderRadius:14, border:"1.5px solid #bbf7d0", padding:"32px", display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                          <div style={{ width:52, height:52, borderRadius:"50%", background:"#dcfce7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.5rem" }}>✅</div>
                          <div style={{ fontWeight:700, color:"#16a34a", fontSize:"0.92rem" }}>Système nominal</div>
                          <div style={{ fontSize:"0.78rem", color:"#94A3B8" }}>Aucune alerte active — tout fonctionne correctement</div>
                        </div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                          {alerts.map((a, i) => {
                            const isError   = a.type === "error"   || a.severity === "high";
                            const isWarning = a.type === "warning" || a.severity === "medium";
                            const color  = isError ? "#dc2626" : isWarning ? "#d97706" : "#7C3AED";
                            const bg     = isError ? "#fee2e2" : isWarning ? "#fef3c7" : "#F5F3FF";
                            const icon   = isError ? "❌" : isWarning ? "⚠️" : "ℹ️";

                            return (
                              <div key={i} style={{ background:"#fff", borderRadius:12, border:`1.5px solid ${color}25`, overflow:"hidden", transition:"all 0.2s" }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor=color; e.currentTarget.style.boxShadow=`0 4px 16px ${color}15`; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor=color+"25"; e.currentTarget.style.boxShadow="none"; }}>
                                <div style={{ height:3, background:`linear-gradient(90deg,${color},${color}80)` }} />
                                <div style={{ padding:"14px 18px", display:"flex", alignItems:"flex-start", gap:12 }}>
                                  <div style={{ width:36, height:36, borderRadius:10, background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>
                                    {icon}
                                  </div>
                                  <div style={{ flex:1 }}>
                                    <div style={{ fontWeight:700, fontSize:"0.86rem", color:"#0F172A", marginBottom:3 }}>
                                      {a.message || a.description || a.type || "Alerte système"}
                                    </div>
                                    <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
                                      {a.facture_num && (
                                        <span style={{ fontSize:"0.7rem", color:"#7C3AED", background:"#F5F3FF", borderRadius:20, padding:"2px 8px", fontWeight:600 }}>
                                          🧾 Facture #{a.facture_num}
                                        </span>
                                      )}
                                      {a.societe && (
                                        <span style={{ fontSize:"0.7rem", color:"#64748B", background:"#F8FAFC", borderRadius:20, padding:"2px 8px", fontWeight:600 }}>
                                          🏢 {a.societe}
                                        </span>
                                      )}
                                      <span style={{ fontSize:"0.68rem", color:"#94A3B8" }}>
                                        📅 {a.date || a.created_at || "—"}
                                      </span>
                                    </div>
                                  </div>
                                  <span style={{ fontSize:"0.68rem", fontWeight:700, padding:"3px 10px", borderRadius:20, color, background:bg, flexShrink:0 }}>
                                    {isError ? "Critique" : isWarning ? "Attention" : "Info"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                        {/* ── Retry queue ── */}
                        {tracabilite?.retry_queue > 0 && (
                          <>
                            <div style={S.sectionTitle}>🔄 File de retry ({tracabilite.retry_queue})</div>
                            <div style={{ background:"#fffbeb", borderRadius:12, border:"1.5px solid #fde68a", padding:"18px 22px", display:"flex", alignItems:"center", gap:16 }}>
                              <div style={{ width:44, height:44, borderRadius:12, background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem" }}>🔄</div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:700, color:"#d97706", fontSize:"0.9rem" }}>{tracabilite.retry_queue} facture{tracabilite.retry_queue > 1 ? "s" : ""} en attente de retry</div>
                                <div style={{ fontSize:"0.75rem", color:"#92400e", marginTop:3 }}>Le système va reessayer automatiquement l'envoi vers ANCE</div>
                              </div>
                              <button style={{ padding:"8px 16px", borderRadius:9, border:"none", background:"#f59e0b", color:"#fff", fontWeight:700, fontSize:"0.8rem", cursor:"pointer", fontFamily:"inherit" }}
                                onClick={() => { fetchAllMiddleware(); showToast("🔄 Retry engine vérifié"); }}>
                                ↻ Vérifier
                              </button>
                            </div>
                          </>
                        )}

                      </div>
                    )}

                    {/* ══ TAB: ALERTES ══ */}
                    {activeTab === "alertes" && (
                      <div style={S.tabContent}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <div style={S.sectionTitle}>🔔 Toutes les alertes système ({alerts.length})</div>
                          {alerts.length > 0 && (
                            <span style={{ fontSize:"0.72rem", fontWeight:700, color:"#e74c3c", background:"#fee2e2", borderRadius:20, padding:"3px 12px" }}>
                              {alerts.length} alerte{alerts.length > 1 ? "s" : ""} active{alerts.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>

                        {alerts.length === 0 ? (
                          <div style={{ background:"#f0fdf4", borderRadius:14, border:"1.5px solid #bbf7d0", padding:"48px", display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
                            <div style={{ width:60, height:60, borderRadius:"50%", background:"#dcfce7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.8rem" }}>✅</div>
                            <div style={{ fontWeight:800, color:"#16a34a", fontSize:"1rem" }}>Système nominal</div>
                            <div style={{ fontSize:"0.8rem", color:"#94A3B8", textAlign:"center" }}>Aucune alerte active — toutes les sociétés fonctionnent correctement</div>
                          </div>
                        ) : (
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {alerts.map((a, i) => {
                              const isError   = a.type==="error"   || a.severity==="high";
                              const isWarning = a.type==="warning" || a.severity==="medium";
                              const color = isError ? "#dc2626" : isWarning ? "#d97706" : "#7C3AED";
                              const bg    = isError ? "#fee2e2" : isWarning ? "#fef3c7" : "#F5F3FF";
                              const icon  = isError ? "❌" : isWarning ? "⚠️" : "ℹ️";

                              return (
                                <div key={i} style={{ background:"#fff", borderRadius:12, border:`1.5px solid ${color}25`, overflow:"hidden", transition:"all 0.2s" }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor=color; e.currentTarget.style.boxShadow=`0 4px 16px ${color}15`; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor=color+"25"; e.currentTarget.style.boxShadow="none"; }}>
                                  <div style={{ height:3, background:`linear-gradient(90deg,${color},${color}80)` }} />
                                  <div style={{ padding:"14px 18px", display:"flex", alignItems:"flex-start", gap:12 }}>
                                    <div style={{ width:38, height:38, borderRadius:10, background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>
                                      {icon}
                                    </div>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontWeight:700, fontSize:"0.86rem", color:"#0F172A", marginBottom:4 }}>
                                        {a.message || a.description || a.type || "Alerte système"}
                                      </div>
                                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                                        {a.facture_num && (
                                          <span style={{ fontSize:"0.7rem", color:"#7C3AED", background:"#F5F3FF", borderRadius:20, padding:"2px 8px", fontWeight:600 }}>
                                            🧾 Facture #{a.facture_num}
                                          </span>
                                        )}
                                        {a.societe && (
                                          <span style={{ fontSize:"0.7rem", color:"#64748B", background:"#F8FAFC", borderRadius:20, padding:"2px 8px", fontWeight:600 }}>
                                            🏢 {a.societe}
                                          </span>
                                        )}
                                        <span style={{ fontSize:"0.68rem", color:"#94A3B8" }}>
                                          📅 {a.date || a.created_at || "—"}
                                        </span>
                                      </div>
                                    </div>
                                    <span style={{ fontSize:"0.68rem", fontWeight:700, padding:"3px 10px", borderRadius:20, color, background:bg, flexShrink:0, border:`1px solid ${color}30` }}>
                                      {isError ? "Critique" : isWarning ? "Attention" : "Info"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                  {/* ══ TAB: AVOIRS ══ */}
                  {activeTab === "avoirs" && (
                    <div style={S.tabContent}>
                      <div style={S.sectionTitle}>📝 Avoirs (Notes de crédit) — Toutes sociétés</div>
                      {middleLoading ? <StateBox loading /> : avoirs.length === 0 ? <EmptyState icon="📝" text="Aucun avoir" /> : (
                        <div style={S.tableCard}>
                          <table style={S.table}>
                            <thead>
                              <tr>{["#","Numéro avoir","Facture liée","Montant","Statut","Date"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {avoirs.map((a, i) => (
                                <tr key={i} style={S.tr}
                                  onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                                  <td style={S.td}><span style={S.idNum}>{i + 1}</span></td>
                                  <td style={S.td}><span style={{ fontWeight: 700, color: "#f59e0b" }}>{a.avoir_number || a.num || "—"}</span></td>
                                  <td style={S.td}><span style={{ color: "#7C3AED", fontWeight: 600 }}>{a.original_invoice_number || a.facture_num || "—"}</span></td>
                                  <td style={S.td}><span style={{ fontWeight: 700 }}>{a.montant_ttc || a.montant ? `${Math.abs(a.montant_ttc || a.montant)} TND` : "—"}</span></td>
                                  <td style={S.td}><StatusBadge status={a.status} /></td>
                                  <td style={S.td}><span style={S.dateCell}>{a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div style={S.sectionTitle}>📴 File d'attente Offline ({offlineQueue.length})</div>
                      {offlineQueue.length === 0 ? (
                        <div style={{ ...S.emptyState, padding: "24px" }}>
                          <p style={{ fontSize: "0.82rem", color: "#94A3B8" }}>✅ Aucune facture en attente de synchronisation</p>
                        </div>
                      ) : (
                        <div style={S.listCard}>
                          {offlineQueue.map((item, i) => (
                            <div key={i} style={S.listRow}>
                              <span style={{ ...S.alertDot, background: "#9b59b6" }} />
                              <span style={{ flex: 1, fontSize: "0.83rem" }}>{item.invoice_number || item.num || `Item ${i + 1}`}</span>
                              <StatusBadge status={item.status} />
                              <span style={S.dateCell}>{item.created_at || ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ══ TAB: SANDBOX ══ */}
                  {activeTab === "sandbox" && (
                    <div style={S.tabContent}>
                      {sandboxRes && (
                        <div className="statsgrid-responsive" style={S.statsGrid4}>
                          {[
                            { label: "Total testées",  value: sandboxRes.total_tested ?? "—",        color: "#7C3AED", sub: "envoyées sandbox" },
                            { label: "Acceptées",       value: sandboxRes.summary?.ACCEPTED ?? "—", color: "#43b89c", sub: "validées"           },
                            { label: "Rejetées",        value: sandboxRes.summary?.REJECTED ?? "—", color: "#e74c3c", sub: "rejetées"           },
                            { label: "En attente",      value: sandboxRes.summary?.PENDING  ?? "—", color: "#f0a500", sub: "en cours"           },
                          ].map((s, i) => <StatCard key={i} {...s} />)}
                        </div>
                      )}

                                    <div style={S.sectionTitle}>🧪 Soumettre au Sandbox ANCE</div>
                      <div style={S.dssCard}>
                        {/* Filtre société */}
                        <div style={{ fontSize:"0.75rem", fontWeight:700, color:"#64748B", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                          Société
                        </div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
                          {companies.map((c, i) => (
                            <button key={i}
                              style={{ padding:"7px 14px", borderRadius:8,
                                border:`2px solid ${sbSociete?.id === c.id ? "#e11d48" : "#E2E8F0"}`,
                                background: sbSociete?.id === c.id ? "#fff1f2" : "#fff",
                                cursor:"pointer", fontSize:"0.78rem", fontWeight:600,
                                color: sbSociete?.id === c.id ? "#e11d48" : "#64748B",
                                fontFamily:"inherit", transition:"all 0.15s" }}
                              onClick={() => setSbSociete(c)}>
                              {c.name || c.nom || `Société ${i+1}`}
                            </button>
                          ))}
                        </div>
                        <div style={S.dssRow}>
                          <input style={S.input} type="number" placeholder="Numéro de facture..." value={sbNumFacture} onChange={e => setSbNumFacture(e.target.value)} />
                          <button style={{ ...S.btnPrimary, background: sbSociete ? "#e11d48" : "#94A3B8", whiteSpace:"nowrap" }}
                            onClick={handleSandboxSubmit} disabled={sbLoading || !sbSociete}>
                            {sbLoading ? "⏳ Soumission..." : "🧪 Envoyer au Sandbox"}
                          </button>
                        </div>
                        {!sbSociete && (
                          <div style={{ fontSize:"0.72rem", color:"#d97706", marginTop:8 }}>
                            ⚠️ Sélectionnez une société avant d'envoyer
                          </div>
                        )}
                      </div>

                      <div style={S.sectionTitle}>📋 Derniers résultats sandbox</div>
                      {!sandboxRes || sandboxRes.invoices?.length === 0 ? <EmptyState icon="🧪" text="Aucun test effectué" /> : (
                        <div style={S.tableCard}>
                          <table style={S.table}>
                            <thead>
                              <tr>{["#","Facture","UUID fiscal","Statut","Env.","Date"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {(sandboxRes.invoices || []).map((inv, i) => (
                                <tr key={i} style={S.tr}
                                  onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                                  <td style={S.td}><span style={S.idNum}>{i + 1}</span></td>
                                  <td style={S.td}><span style={{ fontWeight: 700, color: "#7C3AED" }}>{inv.invoice_number || inv.num || "—"}</span></td>
                                  <td style={S.td}><span style={{ fontSize: "0.72rem", color: "#94A3B8", fontFamily: "monospace" }}>{inv.uuid_dss || inv.uuid || "—"}</span></td>
                                  <td style={S.td}><StatusBadge status={inv.status} /></td>
                                  <td style={S.td}><span style={{ fontSize: "0.75rem", color: "#64748B" }}>{inv.environment || "—"}</span></td>
                                  <td style={S.td}><span style={S.dateCell}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString("fr-FR") : "—"}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

            {/* ══ TAB: EVENT BUS ══ */}
            {activeTab === "events" && (
              <div style={S.tabContent}>

                {/* ── Stats session courante ── */}
                <div className="statsgrid-responsive" style={S.statsGrid4}>
                  {[
                    { label: "Events publiés", value: events.length,                                        color: "#6366f1", sub: "cette session"   },
                    { label: "RabbitMQ",       value: events.filter(e => e.published).length,              color: "#43b89c", sub: "confirmés"        },
                    { label: "Simulés",        value: events.filter(e => !e.published).length,             color: "#f59e0b", sub: "sans RabbitMQ"    },
                    { label: "Dernier event",  value: events[0]?.eventType?.replace("INVOICE_","") || "—", color: "#7C3AED", sub: events[0]?.timestamp || "aucun" },
                  ].map((s, i) => <StatCard key={i} {...s} />)}
                </div>

                {/* ── Formulaire publication manuelle ── */}
                <div style={S.sectionTitle}>📡 Publier un événement</div>
                <div style={S.dssCard}>
                  <div style={{ fontSize:"0.78rem", fontWeight:700, color:"#64748B", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                    Déclencher manuellement un event facture
                  </div>

                  {/* Champ numéro + sélecteur type + bouton publier */}
                  <div style={S.dssRow}>
                    <input
                      style={S.input}
                      type="number"
                      placeholder="N° facture..."
                      value={eventNumFacture}
                      onChange={e => setEventNumFacture(e.target.value)}
                    />
                    <select
                      style={{ ...S.input, flex:"none", width:200, cursor:"pointer" }}
                      value={eventType}
                      onChange={e => setEventType(e.target.value)}
                    >
                      <option value="INVOICE_CREATED">📄 INVOICE_CREATED</option>
                      <option value="INVOICE_UPDATED">✏️ INVOICE_UPDATED</option>
                      <option value="INVOICE_CANCELLED">❌ INVOICE_CANCELLED</option>
                    </select>

                    {/* ✅ FIX : () => handlePublishEvent() évite que React passe
                        le SyntheticEvent comme argument à la place du numéro facture */}
                    <button
                      style={{ ...S.btnPrimary, background:"#6366f1", whiteSpace:"nowrap" }}
                      onClick={() => handlePublishEvent()}
                      disabled={eventLoading}
                    >
                      {eventLoading ? "⏳..." : "📡 Publier"}
                    </button>
                  </div>

                  {/* Cards explicatives des 3 types d'events */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginTop:14 }}>
                    {[
                      { type:"INVOICE_CREATED",   icon:"📄", color:"#6366f1", desc:"Nouvelle facture créée ou soumise à l'ANCE" },
                      { type:"INVOICE_UPDATED",   icon:"✏️",  color:"#f59e0b", desc:"Facture modifiée, statut changé"            },
                      { type:"INVOICE_CANCELLED", icon:"❌", color:"#ef4444", desc:"Facture annulée ou avoir émis"               },
                    ].map((t, i) => (
                      // كليك على card يغير نوع الـ event في الـ select تلقائياً
                      <div key={i}
                        style={{ padding:"12px 14px", borderRadius:10, border:`2px solid ${eventType===t.type ? t.color : "#E2E8F0"}`, background: eventType===t.type ? t.color+"10" : "#F8FAFC", cursor:"pointer", transition:"all 0.15s" }}
                        onClick={() => setEventType(t.type)}
                      >
                        <div style={{ fontSize:"1.1rem", marginBottom:5 }}>{t.icon}</div>
                        <div style={{ fontSize:"0.72rem", fontWeight:800, color: eventType===t.type ? t.color : "#334155" }}>{t.type}</div>
                        <div style={{ fontSize:"0.68rem", color:"#94A3B8", marginTop:3 }}>{t.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Historique events session courante ── */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={S.sectionTitle}>📋 Historique events — session courante ({events.length})</div>
                  {events.length > 0 && (
                    <button style={{ ...S.btnGhost, fontSize:"0.75rem" }} onClick={() => setEvents([])}>
                      🗑️ Vider
                    </button>
                  )}
                </div>

                {/* Empty state si aucun event cette session */}
                {events.length === 0 ? (
                  <div style={{ background:"#F8FAFC", borderRadius:12, border:"1px solid #E8ECEF", padding:"40px", display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                    <div style={{ fontSize:"2.5rem" }}>📡</div>
                    <p style={{ fontSize:"0.84rem", color:"#94A3B8", fontWeight:500 }}>Aucun événement publié cette session</p>
                    <p style={{ fontSize:"0.75rem", color:"#CBD5E1" }}>Les events apparaîtront ici après publication</p>
                  </div>
                ) : (
                  // Liste des events — du plus récent au plus ancien
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {events.map((ev, i) => {
                      // Config visuelle selon le type d'event
                      const cfg = {
                        INVOICE_CREATED:   { icon:"📄", color:"#6366f1", bg:"#eef2ff", label:"Créée"    },
                        INVOICE_UPDATED:   { icon:"✏️",  color:"#f59e0b", bg:"#fffbeb", label:"Modifiée" },
                        INVOICE_CANCELLED: { icon:"❌", color:"#ef4444", bg:"#fff1f2", label:"Annulée"  },
                      }[ev.eventType] || { icon:"🔔", color:"#6366f1", bg:"#eef2ff", label:ev.eventType };

                      return (
                        <div key={ev.id}
                          style={{ background:"#fff", borderRadius:12, border:`1.5px solid ${cfg.color}20`, padding:"14px 18px", display:"flex", alignItems:"center", gap:14, transition:"all 0.2s" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor=cfg.color; e.currentTarget.style.boxShadow=`0 4px 16px ${cfg.color}15`; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor=cfg.color+"20"; e.currentTarget.style.boxShadow="none"; }}>

                          {/* Icône type event */}
                          <div style={{ width:42, height:42, borderRadius:11, background:cfg.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0, border:`2px solid ${cfg.color}25` }}>
                            {cfg.icon}
                          </div>

                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                              <span style={{ fontWeight:800, color:cfg.color, fontSize:"0.88rem" }}>{ev.eventType}</span>
                              <span style={{ fontWeight:700, color:"#7C3AED", fontSize:"0.82rem", fontFamily:"monospace" }}>
                                Facture #{ev.numFacture}
                              </span>
                              {/* Badge : RabbitMQ confirmé ou simulé (fallback) */}
                              <span style={{ fontSize:"0.68rem", fontWeight:700, padding:"2px 8px", borderRadius:20, color: ev.published ? "#16a34a" : "#d97706", background: ev.published ? "#dcfce7" : "#fef3c7" }}>
                                {ev.published ? "✓ RabbitMQ" : "~ Simulé"}
                              </span>
                              {/* Badge Auto : event déclenché automatiquement depuis une action ERP */}
                              {ev.auto && (
                                <span style={{ fontSize:"0.65rem", fontWeight:700, padding:"2px 8px", borderRadius:20, color:"#6366f1", background:"#eef2ff" }}>
                                  ⚡ Auto
                                </span>
                              )}
                            </div>
                            {/* Timestamp + message retour du middleware */}
                            <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:4 }}>
                              📅 {ev.timestamp} · {ev.message}
                            </div>
                          </div>

                          {/* Numéro ordre inverse — le plus récent = #1 */}
                          <span style={{ fontSize:"0.65rem", fontWeight:700, color:"#CBD5E1" }}>#{events.length - i}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Section explicative architecture RabbitMQ ── */}
                <div style={{ background:"#fafafa", borderRadius:12, border:"1px solid #E8ECEF", padding:"16px 20px" }}>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                    ℹ️ À propos de l'Event Bus
                  </div>
                  {/* 4 cards : RabbitMQ / Fallback / Temps réel / Auto-trigger */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    {[
                      { icon:"🐇", title:"RabbitMQ",    desc:"Message broker — distribue les events aux systèmes abonnés"        },
                      { icon:"📡", title:"Fallback",     desc:"Si RabbitMQ hors ligne, l'event est simulé sans erreur"            },
                      { icon:"⚡", title:"Temps réel",   desc:"Les systèmes abonnés reçoivent les events instantanément"          },
                      { icon:"🔁", title:"Auto-trigger", desc:"Les events se déclenchent automatiquement lors des actions ERP"    },
                    ].map((item, i) => (
                      <div key={i} style={{ display:"flex", gap:10, padding:"10px 12px", background:"#fff", borderRadius:9, border:"1px solid #F1F5F9" }}>
                        <span style={{ fontSize:"1.1rem" }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize:"0.76rem", fontWeight:700, color:"#334155" }}>{item.title}</div>
                          <div style={{ fontSize:"0.68rem", color:"#94A3B8", marginTop:2 }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>  
            )}        
      {/* ══ TAB: DEMANDES MODIF ══ */}
      {activeTab === "modifications" && (
        <div style={S.tabContent}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={S.sectionTitle}>✏️ Demandes de modification ({modRequests.length})</div>
            <button style={S.btnSecondary} onClick={fetchModRequests}>↻ Actualiser</button>
          </div>

          {modReqLoading ? <StateBox loading /> : modRequests.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize:"2rem" }}>✅</div>
              <p style={{ fontSize:"0.84rem", color:"#94A3B8" }}>Aucune demande en attente</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {modRequests.map((req, i) => (
                <div key={req.id} style={{ background:"#fff", borderRadius:16, border:"1.5px solid #e8e8f0", padding:"20px 24px", display:"flex", flexDirection:"column", gap:12 }}>
                  
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                    <div>
                      <div style={{ fontWeight:800, fontSize:"0.95rem", color:"#0F172A" }}>
                        ✏️ Modifier <span style={{ color:"#7C3AED" }}>{req.field_name}</span>
                      </div>
                      <div style={{ fontSize:"0.75rem", color:"#94A3B8", marginTop:4 }}>
                        👤 Utilisateur: <strong style={{ color:"#334155" }}>{req.user_name}</strong> ({req.user_email})
                      </div>
                      <div style={{ fontSize:"0.75rem", color:"#94A3B8", marginTop:2 }}>
                        📩 Demandé par: <strong style={{ color:"#334155" }}>{req.requested_by_name}</strong>
                      </div>
                    </div>
                    <span style={{ fontSize:"0.72rem", fontWeight:700, padding:"4px 12px", borderRadius:20, background:"#fef3c7", color:"#d97706" }}>
                      ⏳ En attente
                    </span>
                  </div>

                  {/* Valeurs */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:10, alignItems:"center" }}>
                    <div style={{ background:"#fee2e2", borderRadius:10, padding:"12px 16px" }}>
                      <div style={{ fontSize:"0.6rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:4 }}>Valeur actuelle</div>
                      <div style={{ fontWeight:700, color:"#dc2626", fontSize:"0.9rem" }}>{req.old_value}</div>
                    </div>
                    <div style={{ fontSize:"1.2rem", color:"#94A3B8" }}>→</div>
                    <div style={{ background:"#dcfce7", borderRadius:10, padding:"12px 16px" }}>
                      <div style={{ fontSize:"0.6rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:4 }}>Nouvelle valeur</div>
                      <div style={{ fontWeight:700, color:"#16a34a", fontSize:"0.9rem" }}>{req.new_value}</div>
                    </div>
                  </div>

                  {/* Date */}
                  <div style={{ fontSize:"0.72rem", color:"#94A3B8" }}>
                    📅 {req.created_at ? new Date(req.created_at).toLocaleString("fr-FR") : "—"}
                  </div>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                    <button
                      style={{ padding:"9px 20px", borderRadius:9, border:"none", background: modActionLoading[req.id] ? "#94A3B8" : "#EF4444", color:"#fff", fontWeight:700, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit" }}
                      disabled={modActionLoading[req.id]}
                      onClick={() => handleModAction(req.id, "reject")}>
                      {modActionLoading[req.id] ? "⏳..." : "❌ Rejeter"}
                    </button>
                    <button
                      style={{ padding:"9px 20px", borderRadius:9, border:"none", background: modActionLoading[req.id] ? "#94A3B8" : "#16a34a", color:"#fff", fontWeight:700, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit" }}
                      disabled={modActionLoading[req.id]}
                      onClick={() => handleModAction(req.id, "approve")}>
                      {modActionLoading[req.id] ? "⏳..." : "✅ Approuver"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: ASSIGNER SOCIÉTÉ ══ */}
      {activeTab === "assignments" && (
        <div style={S.tabContent}>

          {/* Header */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={S.sectionTitle}>🏢 Assigner Société à un Admin</div>
            <button style={S.btnSecondary} onClick={() => { fetchAssignments(); fetchAdmins(); }}>
              ↻ Actualiser
            </button>
          </div>

          {/* Form Card */}
          <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #DDD6FE", padding:"24px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:"#F5F3FF", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem" }}>🏢</div>
              <div>
                <div style={{ fontWeight:800, fontSize:"0.92rem", color:"#0F172A" }}>
                  Nouvelle assignation
                </div>
                <div style={{ fontSize:"0.72rem", color:"#94A3B8" }}>
                  Assigner une société à un admin avec commentaire
                </div>
              </div>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

              {/* Select Admin */}
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
                  👤 Admin destinataire *
                </label>
                <select
                  style={{ padding:"10px 14px", borderRadius:9, border:"1.5px solid #E2E8F0", fontSize:"0.84rem", fontFamily:"inherit", outline:"none", color:"#0F172A", background:"#fff" }}
                  value={assignForm.admin_id}
                  onChange={e => setAssignForm(prev => ({ ...prev, admin_id: e.target.value }))}>
                  <option value="">-- Sélectionner un admin --</option>
                  {admins.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Société */}
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
                  🏢 Société à assigner *
                </label>
                <select
                  style={{ padding:"10px 14px", borderRadius:9, border:"1.5px solid #E2E8F0", fontSize:"0.84rem", fontFamily:"inherit", outline:"none", color:"#0F172A", background:"#fff" }}
                  value={assignForm.societe_id}
                  onChange={e => setAssignForm(prev => ({ ...prev, societe_id: e.target.value }))}>
                  <option value="">-- Sélectionner une société --</option>
                  {allPlatformSocietes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nom || c.name} — {c.matricule_fiscal || c.tax_id || "—"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Commentaire */}
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
                  💬 Commentaire / Instructions
                </label>
                <textarea
                  style={{ padding:"10px 14px", borderRadius:9, border:"1.5px solid #E2E8F0", fontSize:"0.84rem", fontFamily:"inherit", outline:"none", color:"#0F172A", minHeight:100, resize:"vertical" }}
                  placeholder="Ex: Gérer les factures du mois de Mars 2026 — priorité haute..."
                  value={assignForm.commentaire}
                  onChange={e => setAssignForm(prev => ({ ...prev, commentaire: e.target.value }))}
                />
              </div>

              {/* Result */}
              {assignResult && (
                <div style={{
                  padding:"12px 16px", borderRadius:9,
                  background: assignResult.success ? "#f0fdf4" : "#fff0f0",
                  border:`1px solid ${assignResult.success ? "#bbf7d0" : "#fecaca"}`,
                  fontSize:"0.82rem", fontWeight:600,
                  color: assignResult.success ? "#16a34a" : "#dc2626"
                }}>
                  {assignResult.success ? "✅" : "❌"} {assignResult.message}
                </div>
              )}

              {/* Submit */}
              <button
                style={{ ...S.btnPrimary, padding:"12px", fontSize:"0.9rem", opacity: assignLoading ? 0.7 : 1 }}
                onClick={handleCreateAssignment}
                disabled={assignLoading}>
                {assignLoading ? "⏳ Envoi en cours..." : "📤 Envoyer l'assignation à l'Admin"}
              </button>
            </div>
          </div>

          {/* Historique assignments */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={S.sectionTitle}>📋 Historique des assignations ({assignments.length})</div>
          </div>

          {assignments.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize:"2rem" }}>🏢</div>
              <p style={{ fontSize:"0.84rem", color:"#94A3B8" }}>Aucune assignation créée</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {assignments.map((a, i) => (
                <div key={i} style={{
                  background:"#fff", borderRadius:12,
                  border:`1.5px solid ${a.statut==="ACCEPTED"?"#bbf7d0":a.statut==="PENDING"?"#fde68a":"#e8e8f0"}`,
                  padding:"16px 20px", display:"flex", flexDirection:"column", gap:10
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:42, height:42, borderRadius:11, background:"#F5F3FF", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem" }}>🏢</div>
                      <div>
                        <div style={{ fontWeight:800, fontSize:"0.92rem", color:"#7C3AED" }}>
                          {a.societe_nom || `Société #${a.societe_id}`}
                        </div>
                        <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:3 }}>
                          👤 Admin: <strong style={{ color:"#334155" }}>{a.admin_name || `Admin #${a.admin_id}`}</strong>
                          {" · "}{a.admin_email}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontSize:"0.75rem", fontWeight:700, padding:"4px 12px", borderRadius:20,
                      background: a.statut==="ACCEPTED"?"#dcfce7":a.statut==="PENDING"?"#fef3c7":"#F8FAFC",
                      color: a.statut==="ACCEPTED"?"#16a34a":a.statut==="PENDING"?"#d97706":"#64748B"
                    }}>
                      {a.statut==="ACCEPTED" ? "✅ Acceptée" : a.statut==="PENDING" ? "⏳ En attente" : a.statut}
                    </span>
                  </div>

                  {a.commentaire && (
                    <div style={{ background:"#F8FAFC", borderRadius:8, padding:"10px 14px", fontSize:"0.8rem", color:"#334155", borderLeft:"3px solid #7C3AED" }}>
                      💬 {a.commentaire}
                    </div>
                  )}

                  {/* Rapport Admin */}
      {a.admin_report && (
        <div style={{ 
          background:"#f0fdf4", borderRadius:8, 
          padding:"10px 14px",
          border:"1px solid #bbf7d0",
          borderLeft:"3px solid #16a34a"
        }}>
          <div style={{ fontSize:"0.6rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:4 }}>
            📋 Rapport Admin
          </div>
          <div style={{ fontSize:"0.82rem", color:"#16a34a", fontWeight:600 }}>
            {a.admin_report}
          </div>
          {a.reported_at && (
            <div style={{ fontSize:"0.68rem", color:"#94A3B8", marginTop:4 }}>
              📅 {new Date(a.reported_at).toLocaleString("fr-FR")}
            </div>
          )}
        </div>
      )}

                  <div style={{ fontSize:"0.7rem", color:"#94A3B8" }}>
                    📅 {a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

                {/* ══ TAB: SYSTÈME ══ */}
                {activeTab === "systeme" && (
                  <div style={S.tabContent}>
                    <div style={S.sectionTitle}>⚙️ État du système</div>
                    <div className="statsgrid-responsive" style={S.statsGrid4}>
                      {[
                        { label: "Certificat ANCE",  value: middleLoading ? "..." : (certOk ? "✓ Valide" : "✗ Expiré"), color: certOk ? "#43b89c" : "#e74c3c", sub: certStatus?.expiry || "—" },
                        { label: "Backend Express",   value: "✓ Online",      color: "#43b89c", sub: "Port 5000"       },
                        { label: "Middleware Python", value: "✓ Online",      color: "#43b89c", sub: "Port 8443 HTTPS" },
                        { label: "Base de données",   value: "✓ PostgreSQL",  color: "#43b89c", sub: "Connexion active"},
                      ].map((s, i) => <StatCard key={i} {...s} />)}

                      <div style={S.sectionTitle}>⚡ Actions rapides</div>
      <div className="quickgrid-responsive" style={S.quickGrid}>
        {[
          { icon: "👤", label: "Mon Profil",     color: "#9b59b6", action: () => setActiveTab("users")    },
          { icon: "🏢", label: "Sociétés",       color: "#e11d48", action: () => setActiveTab("societes") },
          { icon: "🔒", label: "Certificat",     color: "#43b89c", action: () => setActiveTab("systeme")  },
          { icon: "📦", label: "Factures",       color: "#6c63ff", action: () => setActiveTab("factures") },
        ].map((q, i) => (
          <div key={i} style={{ ...S.quickCard, borderColor: q.color + "30" }} className="stat-hover" onClick={q.action}>
            <span style={{ ...S.quickIcon, background: q.color + "15", color: q.color }}>{q.icon}</span>
            <span style={S.quickLabel}>{q.label}</span>
          </div>
        ))}
      </div>
                    

      <div style={S.sectionTitle}>🌉 Configuration Bridge</div>
      <div style={S.dssCard}>
        <div style={{ fontSize:"0.78rem", fontWeight:700, color:"#64748B", marginBottom:10 }}>
          📅 Exercice de traitement (Bridge Paradox)
        </div>
        <div style={S.dssRow}>
          <select
            style={{ ...S.input, cursor:"pointer" }}
            value={bridgeLimit}
            onChange={e => setBridgeLimit(e.target.value)}
          >
            {[2026, 2025, 2024, 2023, 2022].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button style={S.btnPrimary} onClick={() => {
            localStorage.setItem("bridge_exercice", bridgeLimit);
            showToast(`✅ Exercice sauvegardé — ${bridgeLimit}`);
          }}>
            💾 Sauvegarder
          </button>
        </div>
        <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:8 }}>
          Exercice actuel: <strong style={{ color:"#7C3AED" }}>{bridgeLimit}</strong> — change automatiquement chaque année
        </div>
      </div>
      </div>

                    {certStatus && (
                      <>
                        <div style={S.sectionTitle}>🔒 Détails certificat</div>
                        <div style={S.tableCard}>
                          {Object.entries(certStatus).map(([k, v]) => (
                            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "11px 20px", borderBottom: "1px solid #F1F5F9" }}>
                              <span style={{ fontSize: "0.82rem", color: "#64748B", fontWeight: 600 }}>{k}</span>
                              <span style={{ fontSize: "0.82rem", color: "#0F172A", fontWeight: 500 }}>{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* ══ TEST CONNECTIVITÉ SERVEURS ══ */}
      <div style={S.sectionTitle}>🔌 Test de connectivité serveurs</div>
      <ConnectivityTester authH={authH} showToast={showToast} />

                    <div style={S.sectionTitle}>🔗 Endpoints Middleware</div>
                    <div style={S.tableCard}>
                      {[
                        { method: "GET",  path: "/factures",                  desc: "Toutes les factures"         },
                        { method: "GET",  path: "/archive/statistics/full",   desc: "Stats globales archivage"    },
                        { method: "GET",  path: "/tracabilite/alerts",        desc: "Alertes en cours"            },
                        { method: "GET",  path: "/companies",                 desc: "Liste des sociétés"          },
                        { method: "POST", path: "/companies",                 desc: "Créer une société"           },
                        { method: "PUT",  path: "/companies/{id}",            desc: "Modifier une société"        },
                        { method: "POST", path: "/factures/{num}/submit-dss", desc: "Envoyer facture à ANCE"     },
                        { method: "POST", path: "/ance/sandbox/submit/{num}", desc: "Soumettre en sandbox ANCE"  },
                      ].map((e, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: "1px solid #F1F5F9" }}>
                          <span style={{ fontSize: "0.65rem", fontWeight: 800, padding: "2px 7px", borderRadius: 5, background: e.method === "GET" ? "#dbeafe" : e.method === "POST" ? "#fce7f3" : "#fef3c7", color: e.method === "GET" ? "#1d4ed8" : e.method === "POST" ? "#be185d" : "#92400e" }}>
                            {e.method}
                          </span>
                          <span style={{ fontSize: "0.8rem", color: "#334155", fontFamily: "monospace", flex: 1 }}>{e.path}</span>
                          <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>{e.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </main>

            {/* ══ MODAL: CHANGER RÔLE ══ */}
            {roleModal && (
              <div style={S.overlay} onClick={() => !actionLoading && setRoleModal(null)}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                  <div style={S.modalHead}>
                    <div>
                      <div style={S.modalEyebrow}>Modification</div>
                      <h3 style={S.modalTitle}>Changer le rôle</h3>
                    </div>
                    <button style={S.modalX} onClick={() => !actionLoading && setRoleModal(null)}>×</button>
                  </div>
                  <div style={S.modalBody}>
                    <div style={S.modalUserCard}>
                      <div style={{ ...S.avatar, background: "#7C3AED", width: 40, height: 40 }}>
                        {(roleModal.name || roleModal.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.9rem" }}>{roleModal.name || "—"}</div>
                        <div style={{ color: "#94A3B8", fontSize: "0.78rem" }}>{roleModal.email}</div>
                      </div>
                    </div>
                    <div style={S.modalLabel}>Sélectionner le nouveau rôle</div>
                    <div style={S.roleList}>
                      {Object.entries(ROLE_CONFIG).map(([key, rc]) => (
                        <div key={key}
                          style={{ ...S.roleRow, ...(selectedRole === key ? { borderColor: rc.color, background: rc.bg } : {}) }}
                          onClick={() => setSelectedRole(key)}>
                          <div style={{ ...S.roleRowDot, background: selectedRole === key ? rc.color : "#E2E8F0" }} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.88rem", color: selectedRole === key ? rc.color : "#0F172A" }}>{rc.label}</div>
                            <div style={{ fontSize: "0.73rem", color: "#94A3B8" }}>
                              {key === "super_admin" ? "Contrôle total de la plateforme" : key === "admin" ? "Gestion avancée des ressources" : "Accès aux fonctions standard"}
                            </div>
                          </div>
                          {selectedRole === key && <span style={{ marginLeft: "auto", color: rc.color, fontWeight: 800 }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={S.modalFoot}>
                    <button style={S.btnCancel} onClick={() => !actionLoading && setRoleModal(null)}>Annuler</button>
                    <button style={{ ...S.btnPrimary, opacity: (actionLoading || selectedRole === roleModal.role) ? 0.5 : 1 }}
                      onClick={handleChangeRole} disabled={actionLoading || selectedRole === roleModal.role}>
                      {actionLoading ? "Enregistrement..." : "Confirmer"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ══ MODAL: SUPPRIMER USER ══ */}
            {confirmDelete && (
              <div style={S.overlay} onClick={() => !actionLoading && setConfirmDelete(null)}>
                <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                  <div style={S.modalHead}>
                    <div>
                      <div style={{ ...S.modalEyebrow, color: "#EF4444" }}>Action irréversible</div>
                      <h3 style={{ ...S.modalTitle, color: "#EF4444" }}>Supprimer l'utilisateur</h3>
                    </div>
                    <button style={S.modalX} onClick={() => !actionLoading && setConfirmDelete(null)}>×</button>
                  </div>
                  <div style={S.modalBody}>
                    <div style={{ textAlign: "center", padding: "16px 8px" }}>
                      <div style={{ fontSize: "2rem", marginBottom: 12 }}>⚠️</div>
                      <p style={{ fontSize: "0.88rem", color: "#334155", lineHeight: 1.6 }}>
                        Supprimer définitivement <strong style={{ color: "#EF4444" }}>{confirmDelete.email}</strong>
                      </p>
                      <p style={{ fontSize: "0.78rem", color: "#94A3B8", marginTop: 6 }}>Cette action ne peut pas être annulée.</p>
                    </div>
                  </div>
                  <div style={S.modalFoot}>
                    <button style={S.btnCancel} onClick={() => !actionLoading && setConfirmDelete(null)}>Annuler</button>
                    <button style={{ ...S.btnDanger, opacity: actionLoading ? 0.5 : 1 }} onClick={handleDelete} disabled={actionLoading}>
                      {actionLoading ? "Suppression..." : "Supprimer définitivement"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ══ MODAL: CRUD SOCIÉTÉ ══ */}
            {showSocModal && (
              <div style={S.overlay} onClick={() => !socLoading && setShowSocModal(false)}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                  <div style={S.modalHead}>
                    <div>
                      <div style={S.modalEyebrow}>{editSoc ? "Modification" : "Création"}</div>
                      <h3 style={S.modalTitle}>{editSoc ? `Modifier — ${editSoc.name || editSoc.nom}` : "Nouvelle Société"}</h3>
                    </div>
                    <button style={S.modalX} onClick={() => !socLoading && setShowSocModal(false)}>×</button>
                  </div>
                  <div style={S.modalBody}>
                    {[
                      { label: "Nom de la société *", key: "name",   placeholder: "Ex: TPM SARL"          },
                      { label: "Matricule fiscal *",   key: "tax_id", placeholder: "Ex: 1234567B/P/M/000"  },
                      { label: "Adresse",              key: "address",placeholder: "Ex: 12 rue Tunis"      },
                      { label: "Téléphone",            key: "phone",  placeholder: "Ex: +216 71 000 000"   },
                      { label: "Email",                key: "email",  placeholder: "Ex: contact@soc.tn"    },
                    ].map(f => (
                      <div key={f.key} style={{ ...S.formGroup, marginBottom: 12 }}>
                        <label style={S.formLabel}>{f.label}</label>
                        <input style={S.input} placeholder={f.placeholder}
                          value={socForm[f.key] || ""}
                          onChange={e => setSocForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
                      </div>
                    ))}

                    {/* Confirmation 2 étapes pour modification */}
                    {editSoc && !socConfirm && (
                      <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", fontSize: "0.78rem", color: "#92400e" }}>
                        ⚠️ Les modifications seront appliquées immédiatement à la société.
                      </div>
                    )}
                  </div>
                  <div style={S.modalFoot}>
                    <button style={S.btnCancel} onClick={() => !socLoading && setShowSocModal(false)}>Annuler</button>
                    {editSoc && !socConfirm ? (
                      <button style={{ ...S.btnPrimary, background: "#f59e0b" }} onClick={() => setSocConfirm(true)}>
                        📝 Demander modification
                      </button>
                    ) : (
                      <button style={S.btnPrimary} onClick={handleSaveSoc} disabled={socLoading || !socForm.name}>
                        {socLoading ? "⏳ Sauvegarde..." : editSoc ? "✅ Valider & Confirmer" : "➕ Créer la société"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ══ MODAL: SUPPRIMER SOCIÉTÉ ══ */}
            {confirmDelSoc && (
              <div style={S.overlay} onClick={() => !socLoading && setConfirmDelSoc(null)}>
                <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                  <div style={S.modalHead}>
                    <div>
                      <div style={{ ...S.modalEyebrow, color: "#EF4444" }}>Action irréversible</div>
                      <h3 style={{ ...S.modalTitle, color: "#EF4444" }}>Supprimer la société</h3>
                    </div>
                    <button style={S.modalX} onClick={() => !socLoading && setConfirmDelSoc(null)}>×</button>
                  </div>
                  <div style={S.modalBody}>
                    <div style={{ textAlign: "center", padding: "16px 8px" }}>
                      <div style={{ fontSize: "2rem", marginBottom: 12 }}>🏢⚠️</div>
                      <p style={{ fontSize: "0.88rem", color: "#334155", lineHeight: 1.6 }}>
                        Supprimer définitivement <strong style={{ color: "#EF4444" }}>{confirmDelSoc.name || confirmDelSoc.nom}</strong> ?
                      </p>
                      <p style={{ fontSize: "0.78rem", color: "#94A3B8", marginTop: 6 }}>Toutes les données associées seront perdues.</p>
                    </div>
                  </div>
                  <div style={S.modalFoot}>
                    <button style={S.btnCancel} onClick={() => !socLoading && setConfirmDelSoc(null)}>Annuler</button>
                    <button style={{ ...S.btnDanger, opacity: socLoading ? 0.5 : 1 }} onClick={handleDeleteSoc} disabled={socLoading}>
                      {socLoading ? "Suppression..." : "Supprimer définitivement"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {mobileMenuOpen && (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 99 }} onClick={() => setMobileMenuOpen(false)} />
)}

{/* TOAST */}
            {toast && (
              <div style={{ ...S.toast, ...(toast.type === "error" ? S.toastErr : S.toastOk) }} className="toast-in">
                <span style={S.toastDot} />
                {toast.msg}
              </div>
            )}
          </div>
        );
      }

      // ─── SUB-COMPOSANTS ───────────────────────────────────────────
      function StatCard({ label, value, color, sub }) {
        return (
          <div style={S.statCard} className="stat-hover">
            <div style={S.statTop}>
              <span style={S.statLabel}>{label}</span>
              <div style={{ ...S.statDot, background: color }} />
            </div>
            <div style={{ ...S.statValue, color }}>{value}</div>
            <div style={S.statSub}>{sub}</div>
          </div>
        );
      }

      function StatusBadge({ status }) {
        const s = (status || "").toUpperCase();
      const map = {
          C:         { color: "#2563eb", bg: "#dbeafe", label: "📝 Créée"     },
          V:         { color: "#16a34a", bg: "#dcfce7", label: "✅ Validée"   },
          ARCHIVED:  { color: "#e11d48", bg: "#ffe4e6", label: "🗄️ Archivée" },
          ACCEPTED:  { color: "#16a34a", bg: "#dcfce7", label: "✓ Acceptée"   },
          REJECTED:  { color: "#dc2626", bg: "#fee2e2", label: "✗ Rejetée"    },
          PENDING:   { color: "#d97706", bg: "#fef3c7", label: "⏳ En attente" },
          SUBMITTED: { color: "#0ea5e9", bg: "#e0f2fe", label: "📤 Soumise"    },
          SIGNED:    { color: "#7c3aed", bg: "#f5f3ff", label: "✍ Signée"     },
          CREDITED:  { color: "#9b59b6", bg: "#f5f3ff", label: "↩ Creditée"   },
          SYNCED:    { color: "#43b89c", bg: "#dcfce7", label: "✓ Syncée"     },
          FAILED:    { color: "#dc2626", bg: "#fee2e2", label: "✗ Échouée"    },
      };
        const cfg = map[s] || { color: "#94A3B8", bg: "#F8FAFC", label: status || "—" };
        return (
          <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 20, fontSize: "0.71rem", fontWeight: 700, color: cfg.color, background: cfg.bg }}>
            {cfg.label}
          </span>
        );
      }

      function StateBox({ loading, empty }) {
        return (
          <div style={S.stateBox}>
            {loading && <div style={S.spinnerRing} className="spin" />}
            {empty   && <div style={{ fontSize: "2rem", color: "#CBD5E1" }}>∅</div>}
            <p style={S.stateText}>{loading ? "Chargement..." : "Aucun résultat"}</p>
          </div>
        );
      }

      function EmptyState({ icon, text }) {
        return (
          <div style={S.emptyState}>
            <div style={{ fontSize: "2.5rem" }}>{icon}</div>
            <p style={{ fontSize: "0.84rem", color: "#94A3B8", fontWeight: 500 }}>{text}</p>
          </div>
        );
      }

      function ConnectivityTester({ authH, showToast }) {
        const SERVERS = [
          { key: "bridge", label: "Bridge Paradox", icon: "🌉", color: "#6c63ff", defaultUrl: "http://197.13.0.212:9000", testPath: "/", canRestart: false, canRemoteRestart: true },
          { key: "middleware", label: "Middleware TEIF",    icon: "⚙️", color: "#7C3AED", defaultUrl: "https://das-cloud.com:8443",    testPath: "/health"  },
          { key: "backend",    label: "Backend Auth",       icon: "🔐", color: "#0EA5E9", defaultUrl: "http://das-cloud.com:5000",     testPath: "/" },
        ];

        const [urls,    setUrls]    = useState(() => {
          const saved = {};
          SERVERS.forEach(s => { saved[s.key] = localStorage.getItem(`server_url_${s.key}`) || s.defaultUrl; });
          return saved;
        });
        const [results, setResults] = useState({});
        const [testing, setTesting] = useState({});

        const testOne = async (server) => {
          setTesting(prev => ({ ...prev, [server.key]: true }));
          setResults(prev => ({ ...prev, [server.key]: null }));
          const start = Date.now();
          try {
            const res = await fetch(`${urls[server.key]}${server.testPath}`, {
              headers: authH(),
              signal: AbortSignal.timeout(5000)
            });
            const latency = Date.now() - start;
            const data    = await res.json().catch(() => ({}));
            setResults(prev => ({
              ...prev,
              [server.key]: {
                ok:      res.ok,
                status:  res.status,
                latency,
                info:    data?.service || data?.status || data?.message || "OK",
                version: data?.version || null,
              }
            }));
          } catch (err) {
            setResults(prev => ({
              ...prev,
              [server.key]: { ok: false, status: 0, latency: Date.now() - start, info: err.message || "Connexion refusée" }
            }));
          }
          setTesting(prev => ({ ...prev, [server.key]: false }));
        };

        const testAll = () => SERVERS.forEach(s => testOne(s));

        const saveUrl = (key, val) => {
          localStorage.setItem(`server_url_${key}`, val);
          showToast(`✅ URL ${key} sauvegardée`);
        };

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <button style={{ padding:"8px 18px", borderRadius:9, border:"none", background:"#7C3AED", color:"#fff", fontWeight:700, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit" }}
                onClick={testAll}>
                🔌 Tester tous les serveurs
              </button>
            </div>

            {SERVERS.map(server => {
              const r = results[server.key];
              const t = testing[server.key];
              return (
                <div key={server.key} style={{ background:"#fff", borderRadius:12, border:`1.5px solid ${r ? (r.ok ? "#bbf7d0" : "#fecaca") : "#E8ECEF"}`, padding:"18px 20px", transition:"all 0.2s" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:server.color+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>
                      {server.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:"0.9rem", color:"#0F172A" }}>{server.label}</div>
                      <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:2 }}>{urls[server.key]}</div>
                    </div>
                    {/* Status badge */}
                    <div style={{ padding:"4px 12px", borderRadius:20, fontSize:"0.72rem", fontWeight:700,
                      background: t ? "#fef3c7" : !r ? "#F8FAFC" : r.ok ? "#dcfce7" : "#fee2e2",
                      color:      t ? "#d97706" : !r ? "#94A3B8" : r.ok ? "#16a34a" : "#dc2626" }}>
                      {t ? "⏳ Test..." : !r ? "— Non testé" : r.ok ? `✅ Online · ${r.latency}ms` : `❌ Offline`}
                    </div>
                  </div>

                  {/* URL éditable */}
                  <div style={{ display:"flex", gap:8, marginBottom: r ? 10 : 0 }}>
                    <input
                      style={{ flex:1, padding:"8px 12px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:"0.8rem", fontFamily:"monospace", outline:"none", color:"#334155" }}
                      value={urls[server.key]}
                      onChange={e => setUrls(prev => ({ ...prev, [server.key]: e.target.value }))}
                      placeholder="http://..."
                    />
                    <button style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#F8FAFC", cursor:"pointer", fontSize:"0.8rem", fontWeight:600, color:"#64748B", fontFamily:"inherit" }}
                      onClick={() => saveUrl(server.key, urls[server.key])}>
                      💾
                    </button>
                    <button style={{ padding:"8px 14px", borderRadius:8, border:"none", background:server.color, cursor:"pointer", fontSize:"0.8rem", fontWeight:700, color:"#fff", fontFamily:"inherit" }}
                      onClick={() => testOne(server)} disabled={t}>
                      {t ? "⏳" : "Tester"}
                    </button>
                  </div>

                  {server.canRemoteRestart && (
        <div style={{ display:"flex", gap:6, marginTop:8 }}>
          <button
            style={{ flex:1, padding:"7px 0", borderRadius:8, border:"none",
              background:"#f0f9ff", color:"#0891b2", fontWeight:700,
              fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit" }}
            onClick={async () => {
              try {
                showToast("⏳ Restart en cours...");
                await fetch(
                  `${urls[server.key]}/admin/restart?secret=LOGIMES_SECRET_2026`,
                  { method:"POST" }
                );
                showToast("🔄 Bridge redémarre — test dans 6s...");
              } catch {
                // Normal — bridge saker w ma yrodch response
                showToast("🔄 Bridge redémarre — test dans 6s...");
              }
              setTimeout(() => testOne(server), 12000);
            }}>
            🔄 Restart Bridge
          </button>
        </div>
      )}

                  {/* Résultat détaillé */}
                  {r && (
                    <div style={{ background: r.ok ? "#f0fdf4" : "#fff0f0", borderRadius:8, padding:"10px 14px", display:"flex", gap:16, flexWrap:"wrap" }}>
                      <span style={{ fontSize:"0.75rem", color:"#64748B" }}>
                        Status: <strong style={{ color: r.ok ? "#16a34a" : "#dc2626" }}>{r.status || "ERR"}</strong>
                      </span>
                      <span style={{ fontSize:"0.75rem", color:"#64748B" }}>
                        Latence: <strong style={{ color:"#7C3AED" }}>{r.latency}ms</strong>
                      </span>
                      {r.version && (
                        <span style={{ fontSize:"0.75rem", color:"#64748B" }}>
                          Version: <strong style={{ color:"#0F172A" }}>{r.version}</strong>
                        </span>
                      )}
                      <span style={{ fontSize:"0.75rem", color:"#64748B", flex:1 }}>
                        Info: <strong style={{ color:"#334155" }}>{r.info}</strong>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // ─── STYLES ───────────────────────────────────────────────────
      const S = {
        root: { display: "flex", minHeight: "100vh", background: "#F8FAFC", fontFamily: "'Outfit','Segoe UI',sans-serif", color: "#0F172A" },

        sidebar:       { flexShrink: 0, background: "#fff", borderRight: "1px solid #E8ECEF", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 100, transition: "width 0.25s ease", overflow: "hidden" },
        brand:         { display: "flex", alignItems: "center", gap: 10, padding: "22px 16px 10px", cursor: "pointer", borderBottom: "1px solid #F1F5F9", flexShrink: 0 },
        brandMark:     { width: 32, height: 32, background: "linear-gradient(135deg,#7C3AED,#6D28D9)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 8px rgba(124,58,237,0.35)" },
        brandName:     { fontSize: "0.95rem", fontWeight: 800, letterSpacing: "0.08em", color: "#0F172A", whiteSpace: "nowrap" },
        godBadge:      { background: "linear-gradient(90deg,#7C3AED,#9333EA)", color: "#fff", fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.15em", padding: "4px 16px", textAlign: "center", flexShrink: 0 },
        collapseBtn:   { background: "#F5F3FF", border: "none", color: "#7C3AED", fontSize: "0.75rem", cursor: "pointer", padding: "4px 8px", borderRadius: 6, margin: "6px 10px 4px", alignSelf: "flex-end", flexShrink: 0 },
        sidebarNav:    { flex: 1, overflowY: "auto", padding: "4px 8px" },
        sectionLabel:  { display: "flex", alignItems: "center", gap: 6, fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "10px 8px 3px", whiteSpace: "nowrap" },
        sectionDot:    { width: 5, height: 5, borderRadius: "50%", flexShrink: 0 },
        sectionDivider:{ height: 1, background: "#ededf5", margin: "6px" },
        navItem:       { display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 8, cursor: "pointer", transition: "all 0.15s", marginBottom: 1, fontSize: "0.83rem", fontWeight: 500, whiteSpace: "nowrap", borderLeft: "3px solid transparent" },
        navIcon:       { fontSize: "0.9rem", width: 20, textAlign: "center", flexShrink: 0 },
        navLabel:      { overflow: "hidden", textOverflow: "ellipsis" },
        sidebarFooter: { borderTop: "1px solid #F1F5F9", padding: "10px", flexShrink: 0 },
        userCard:      { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, background: "#F8FAFC", marginBottom: 6 },
        userInitials:  { width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#7C3AED,#9333EA)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 800, flexShrink: 0 },
        userMeta:      { overflow: "hidden" },
        userNameText:  { fontSize: "0.78rem", fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
        userRoleTag:   { fontSize: "0.63rem", color: "#7C3AED", fontWeight: 600, marginTop: 1 },
        logoutBtn:     { display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: "#64748B", fontFamily: "inherit", transition: "all 0.15s", justifyContent: "center" },

        main:          { flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", transition: "margin-left 0.25s ease" },
        header:        { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 32px", background: "#fff", borderBottom: "1px solid #E8ECEF", position: "sticky", top: 0, zIndex: 50, flexWrap: "wrap", gap: 12, rowGap: 10 },
        headerActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
        headerEyebrow: { fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.08em", color: "#94A3B8", textTransform: "uppercase", marginBottom: 3 },
        headerTitle:   { fontSize: "1.25rem", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" },

        tabsBar:      { display: "flex", padding: "0 32px", background: "#fff", borderBottom: "1px solid #E8ECEF", overflowX: "auto" },
        tabBtn:       { display: "flex", alignItems: "center", gap: 6, padding: "12px 14px", border: "none", borderBottom: "2px solid transparent", background: "none", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, color: "#64748B", fontFamily: "inherit", transition: "all 0.15s", position: "relative", whiteSpace: "nowrap" },
        tabBtnActive: { color: "#7C3AED", borderBottomColor: "#7C3AED" },
        tabBadge:     { background: "#EF4444", color: "#fff", fontSize: "0.6rem", fontWeight: 800, padding: "1px 5px", borderRadius: 20 },

        content:      { padding: "24px 32px", flex: 1 },
        tabContent:   { display: "flex", flexDirection: "column", gap: 16 },
        sectionTitle: { fontSize: "0.78rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 4 },

        statsGrid4:  { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 },
        statCard:    { background: "#fff", borderRadius: 12, padding: "18px 20px", border: "1px solid #E8ECEF", transition: "all 0.2s" },
        statTop:     { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
        statLabel:   { fontSize: "0.7rem", fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" },
        statDot:     { width: 7, height: 7, borderRadius: "50%" },
        statValue:   { fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1 },
        statSub:     { fontSize: "0.7rem", color: "#CBD5E1", marginTop: 4, fontWeight: 500 },

        quickGrid:   { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 },
        quickCard:   { background: "#fff", border: "1.5px solid #e8e8f0", borderRadius: 12, padding: "16px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, transition: "all 0.2s" },
        quickIcon:   { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" },
        quickLabel:  { fontSize: "0.78rem", fontWeight: 600, color: "#334155", textAlign: "center" },

        toolbar:     { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
        searchBox:   { flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 9, padding: "0 12px", height: 40 },
        searchInput: { flex: 1, border: "none", outline: "none", fontSize: "0.84rem", background: "transparent", color: "#0F172A", fontFamily: "inherit" },
        searchClear: { background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "1.1rem" },
        filterGroup:      { display: "flex", gap: 6 },
        filterChip:       { padding: "8px 14px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: "#64748B", fontFamily: "inherit", transition: "all 0.15s" },
        filterChipActive: { background: "#7C3AED", color: "#fff", borderColor: "#7C3AED" },
        resultMeta:   { display: "flex", gap: 6, alignItems: "center" },
        resultCount:  { fontSize: "0.78rem", fontWeight: 700, color: "#334155" },

        tableCard:   { background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", overflow: "hidden", overflowX: "auto" },
        table:       { width: "100%", borderCollapse: "collapse" },
        th:          { padding: "11px 16px", textAlign: "left", fontSize: "0.67rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: "1px solid #F1F5F9", background: "#FAFAFA" },
        tr:          { borderBottom: "1px solid #F8FAFC", transition: "background 0.1s" },
        td:          { padding: "12px 16px", verticalAlign: "middle" },
        idNum:       { fontSize: "0.75rem", color: "#CBD5E1", fontWeight: 700 },
        userCell:    { display: "flex", alignItems: "center", gap: 10 },
        avatar:      { width: 32, height: 32, borderRadius: 8, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.82rem", fontWeight: 800, flexShrink: 0 },
        userName3:   { fontSize: "0.86rem", fontWeight: 700, color: "#0F172A" },
        youBadge:    { background: "#DCFCE7", color: "#16A34A", fontSize: "0.62rem", fontWeight: 700, padding: "1px 6px", borderRadius: 20, display: "inline-block", marginTop: 2 },
        emailCell:   { fontSize: "0.8rem", color: "#64748B" },
        roleChip:    { display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: "0.73rem", fontWeight: 700, border: "1px solid" },
        dateCell:    { fontSize: "0.77rem", color: "#94A3B8" },
        actionsRow:  { display: "flex", gap: 6, alignItems: "center" },
        actionEdit:  { padding: "6px 12px", borderRadius: 7, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, color: "#7C3AED", fontFamily: "inherit", transition: "all 0.15s" },
        actionDelete:{ padding: "6px 8px", borderRadius: 7, border: "1px solid #FEE2E2", background: "#FFF5F5", cursor: "pointer", color: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" },

        listCard:    { background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", overflow: "hidden" },
        listRow:     { display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", borderBottom: "1px solid #F8FAFC" },
        alertDot:    { width: 6, height: 6, borderRadius: "50%", background: "#EF4444", flexShrink: 0 },
        moreLink:    { padding: "10px 16px", fontSize: "0.78rem", color: "#7C3AED", fontWeight: 600, cursor: "pointer", textAlign: "center" },

        emptyState:  { background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "40px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
        stateBox:    { background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "48px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
        spinnerRing: { width: 32, height: 32, border: "3px solid #F1F5F9", borderTop: "3px solid #7C3AED", borderRadius: "50%" },
        stateText:   { fontSize: "0.84rem", color: "#94A3B8", fontWeight: 500 },

        dssCard:     { background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "20px" },
        dssRow:      { display: "flex", gap: 10, alignItems: "center" },
        input:       { flex: 1, padding: "9px 14px", borderRadius: 9, border: "1px solid #E2E8F0", fontSize: "0.84rem", fontFamily: "inherit", outline: "none", color: "#0F172A" },
        resultBox:   { marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: "0.82rem", fontWeight: 500 },

        formGroup:   { display: "flex", flexDirection: "column", gap: 4 },
        formLabel:   { fontSize: "0.75rem", fontWeight: 600, color: "#64748B" },

        pill:        { padding: "4px 12px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap" },
        pillGreen:   { background: "#dcfce7", color: "#16a34a" },
        pillRed:     { background: "#fee2e2", color: "#dc2626" },

        btnSecondary:{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 9, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: "#334155", fontFamily: "inherit", transition: "all 0.15s" },
        btnGhost:    { padding: "8px 16px", borderRadius: 9, border: "1px solid transparent", background: "none", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: "#64748B", fontFamily: "inherit", transition: "all 0.15s" },
        btnPrimary:  { padding: "9px 18px", borderRadius: 9, border: "none", background: "#7C3AED", cursor: "pointer", fontSize: "0.82rem", fontWeight: 700, color: "#fff", fontFamily: "inherit" },
        btnCancel:   { padding: "9px 18px", borderRadius: 9, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: "#64748B", fontFamily: "inherit" },
        btnDanger:   { padding: "9px 18px", borderRadius: 9, border: "none", background: "#EF4444", cursor: "pointer", fontSize: "0.82rem", fontWeight: 700, color: "#fff", fontFamily: "inherit" },

        overlay:      { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" },
        modal:        { background: "#fff", borderRadius: 16, width: "90%", maxWidth: 500, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" },
        modalHead:    { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px", borderBottom: "1px solid #F1F5F9" },
        modalEyebrow: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", color: "#94A3B8", textTransform: "uppercase", marginBottom: 3 },
        modalTitle:   { fontSize: "1rem", fontWeight: 800, color: "#0F172A" },
        modalX:       { background: "#F8FAFC", border: "1px solid #E2E8F0", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
        modalBody:    { padding: "20px 24px" },
        modalUserCard:{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#F8FAFC", borderRadius: 10, marginBottom: 18, border: "1px solid #F1F5F9" },
        modalLabel:   { fontSize: "0.75rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 },
        roleList:     { display: "flex", flexDirection: "column", gap: 8 },
        roleRow:      { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: "1.5px solid #E2E8F0", cursor: "pointer", transition: "all 0.15s" },
        roleRowDot:   { width: 10, height: 10, borderRadius: "50%", flexShrink: 0, transition: "background 0.15s" },
        modalFoot:    { display: "flex", gap: 8, padding: "16px 24px", borderTop: "1px solid #F1F5F9", justifyContent: "flex-end" },

        toast:   { position: "fixed", bottom: 24, right: 24, display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderRadius: 10, fontSize: "0.84rem", fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 2000 },
        toastOk: { background: "#0F172A", color: "#fff" },
        toastErr:{ background: "#EF4444", color: "#fff" },
        toastDot:{ width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.5)", flexShrink: 0 },
      };

      const css = `
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body {
          overflow-x: hidden;
          width: 100%;
          max-width: 100vw;
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 2px; }
        .nav-hover:hover    { background: #F5F3FF !important; color: #7C3AED !important; }
        .btn-hover:hover    { background: #F8FAFC !important; border-color: #CBD5E1 !important; }
        .logout-hover:hover { background: #FFF5F5 !important; color: #EF4444 !important; border-color: #FEE2E2 !important; }
        .stat-hover:hover   { box-shadow: 0 4px 20px rgba(0,0,0,0.07); transform: translateY(-2px); }
        .chip-hover:hover   { background: #F8FAFC !important; border-color: #CBD5E1 !important; }
        .toast-in { animation: toastIn 0.3s ease; }
        @keyframes toastIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }

        /* ══════════ RESPONSIVE MOBILE ══════════ */
        @media (max-width: 768px) {
          .sidebar-responsive { 
            transform: translateX(-100%) !important; 
            transition: transform 0.25s ease !important;
          }
          .sidebar-responsive.open { transform: translateX(0) !important; }
          .main-responsive { margin-left: 0 !important; }
          .header-responsive { padding: 14px 16px !important; flex-wrap: wrap !important; }
          .content-responsive { padding: 14px 16px !important; }
          .tabsbar-responsive { padding: 0 16px !important; }
          .statsgrid-responsive { grid-template-columns: 1fr !important; }
          .quickgrid-responsive { grid-template-columns: repeat(2,1fr) !important; }
          .table-responsive { overflow-x: auto !important; display: block !important; }
          .mobile-menu-btn { display: flex !important; }
          .header-responsive > div:last-child {
            flex-wrap: wrap !important;
            width: 100% !important;
            justify-content: flex-start !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-menu-btn { display: none !important; }
        }
      `;